/* =====================================================================
   Cave Larégnère — SOMMELIER NUMÉRIQUE · BASE DE SAVOIR
   =====================================================================
   Ce fichier ne contient AUCUNE logique : uniquement le savoir du
   caviste, écrit en clair, que le moteur (cl-sommelier-moteur.js) va
   lire. On peut donc corriger un accord, ajouter un plat ou changer un
   barème de quantité sans jamais toucher au moteur ni à l'interface.

   TROIS FAÇONS DE FAIRE ÉVOLUER L'OUTIL, DE LA PLUS SIMPLE À LA PLUS FINE
   1. Dans l'éditeur Shopify (section « Sommelier — Mon menu & mes
      bouteilles ») : textes, barèmes, gammes, budgets, produits mis en
      avant, exclusions. Aucun code.
   2. Dans l'éditeur également, le champ « Règles additionnelles (JSON) »
      permet d'ajouter des plats ou de corriger un accord sans toucher à
      ce fichier. Ce qui y est écrit ÉCRASE ce qui suit.
   3. Ici, pour un enrichissement durable de la bibliothèque de plats.

   ---------------------------------------------------------------------
   COMMENT SE LIT UN PLAT
   ---------------------------------------------------------------------
   `axes` décrit ce que le plat FAIT en bouche, sur une échelle de 0 à 3.
   C'est ce qui permet de traiter un plat inconnu par ressemblance plutôt
   que par correspondance exacte.

     gras ....... matière grasse, onctuosité (appelle acidité ou tanin)
     iode ....... mer, coquillage, salinité marine
     sucre ...... sucré du plat (dessert, sucré-salé, chutney)
     epice ...... piment et épices fortes (le tanin et l'alcool les avivent)
     tanin ...... aptitude du plat à absorber le tanin (protéines fermes)
     umami ...... champignon, braisé, affiné, tomate cuite, soja
     acide ...... vinaigrette, agrume, tomate crue, cornichon
     fume ....... grillé, barbecue, fumaison
     sel ........ salant marqué (bleu, anchois, olive, jambon sec)
     amer ....... amertume (endive, cacao, café, chocolat noir)
     puissance .. intensité générale du plat, de la plus délicate (0) à
                  la plus démonstrative (3) — c'est elle qui pilote la
                  progression du repas.

   `familles` note l'affinité de chaque famille de boisson avec ce plat,
   de 0 à 100. Ce n'est pas une vérité absolue (règle n°23) : c'est une
   préférence de caviste, que le client peut toujours remplacer.

   `deconseille` note les fausses bonnes idées : les familles que ce plat
   met en difficulté, avec la raison. Le moteur les pénalise, et si l'une
   d'elles finit malgré tout retenue (parce que le client l'a demandée,
   ou que la cave n'a rien d'autre), il le dit au lieu de le taire.

   `raisons` donne l'explication à afficher. Elle est concrète et propre
   au couple plat/famille — jamais un texte passe-partout (règle n°18).
   ===================================================================== */
(function (w) {
  'use strict';

  var S = {};

  S.version = '7.0.0';

  /* -------------------------------------------------------------------
     LES ÉTAPES DU REPAS
     `poids` = part du volume total qui revient à cette étape quand elle
     est présente. Les poids sont renormalisés sur les seules étapes
     réellement choisies : un repas sans fromage ne « perd » pas sa part,
     elle est redistribuée.
     `ordre` sert au contrôle de progression (du plus léger au plus
     puissant).
     ------------------------------------------------------------------- */
  S.etapes = [
    { id: 'apero',    nom: 'Apéritif',      ordre: 1, poids: 20, exemple: 'huîtres, saumon fumé, gougères…' },
    { id: 'entree',   nom: 'Entrée',        ordre: 2, poids: 16, exemple: 'foie gras, velouté, terrine…' },
    { id: 'plat',     nom: 'Plat principal',ordre: 3, poids: 34, exemple: 'magret aux cèpes, poisson rôti…' },
    { id: 'fromage',  nom: 'Fromages',      ordre: 4, poids: 12, exemple: 'comté, roquefort, chèvre frais…' },
    { id: 'dessert',  nom: 'Dessert',       ordre: 5, poids: 11, exemple: 'fondant au chocolat, tarte aux fruits…' },
    { id: 'digestif', nom: 'Digestif',      ordre: 6, poids: 7,  exemple: 'armagnac, rhum vieux, calvados…' }
  ];

  /* -------------------------------------------------------------------
     NIVEAUX DE CONSOMMATION
     ---------------------------------------------------------------------
     Le point de départ n'est pas une estimation maison : ce sont les
     DOSES DE SERVICE DU BAR ET DU RESTAURANT, celles que verse un
     professionnel. Elles sont portées par chaque famille de boisson,
     dans son champ `verre` :

       verre de vin servi à table ........ 12,5 cl  (bouteille = 6 verres)
       flûte de champagne ................ 10 cl    (bouteille = 7 à 8)
       verre de vin doux / liquoreux ..... 7 à 8 cl
       dose bar de spiritueux ............ 4 cl     (bouteille 70 cl = 17)
       digestif servi court .............. 3 cl
       bière .............................  25 cl
       soft, jus, tonic ..................  20 cl

     `cl` ci-dessous est le volume de référence par adulte consommant de
     l'alcool, pour un repas de deux heures — soit un nombre de verres
     STANDARD, que le client ajuste ensuite d'un clic selon ses convives.
     La durée réelle du repas module encore ce volume.

     Le vocabulaire ne juge personne (règle du brief) : on parle du
     service, pas des gens.
     ------------------------------------------------------------------- */
  S.niveaux_conso = [
    { id: 'legere',         nom: 'Légère',         court: 'Léger',         cl: 25,   verres: 2,   detail: 'environ 2 verres par personne' },
    { id: 'moderee',        nom: 'Modérée',        court: 'Modéré',        cl: 37.5, verres: 3,   detail: 'environ 3 verres par personne' },
    { id: 'genereuse',      nom: 'Généreuse',      court: 'Généreux',      cl: 50,   verres: 4,   detail: 'environ 4 verres par personne' },
    { id: 'tres_genereuse', nom: 'Très généreuse', court: 'Très généreux', cl: 68.75, verres: 5.5, detail: 'environ 5 à 6 verres par personne' }
  ];

  /* Volume de boissons sans alcool prévu par convive qui n'en consomme
     pas — un vrai parcours, pas une variable d'ajustement (règle n°9). */
  S.cl_sans_alcool_par_personne = 60;

  /* -------------------------------------------------------------------
     TYPES DE REPAS
     `facteur` ajuste le volume ; `duree` propose une durée par défaut ;
     `etapes` pré-coche un menu type ; `ambiance` oriente les accords
     (un mariage appelle des bulles, un repas professionnel de la
     mesure).
     ------------------------------------------------------------------- */
  S.types_repas = [
    { id: 'dejeuner',  nom: 'Déjeuner',            facteur: 0.85, duree: 90,  etapes: ['entree','plat','fromage','dessert'] },
    { id: 'diner',     nom: 'Dîner',               facteur: 1.00, duree: 150, etapes: ['apero','entree','plat','fromage','dessert'] },
    { id: 'aperitif',  nom: 'Apéritif dînatoire',  facteur: 0.90, duree: 150, etapes: ['apero'] },
    { id: 'buffet',    nom: 'Buffet',              facteur: 1.00, duree: 180, etapes: ['apero','plat','dessert'] },
    { id: 'mariage',   nom: 'Mariage',             facteur: 1.25, duree: 300, etapes: ['apero','entree','plat','fromage','dessert'], bulles: true },
    { id: 'anniversaire', nom: 'Anniversaire',     facteur: 1.15, duree: 210, etapes: ['apero','plat','dessert'], bulles: true },
    { id: 'fete',      nom: 'Fête',                facteur: 1.15, duree: 240, etapes: ['apero','plat','dessert'], bulles: true },
    { id: 'pro',       nom: 'Repas professionnel', facteur: 0.75, duree: 120, etapes: ['entree','plat','dessert'], sobre: true }
  ];

  /* -------------------------------------------------------------------
     LES TROIS GAMMES
     Elles ne sont PAS des tranches de prix figées : le moteur les
     calcule sur les candidats réellement disponibles pour chaque étape
     (percentiles). Une cave qui s'enrichit fait donc bouger les gammes
     toute seule. `cible` = position visée dans la liste des candidats
     triés par prix, de 0 (le plus accessible) à 1 (le plus rare).
     ------------------------------------------------------------------- */
  S.gammes = [
    { id: 'essentiel', nom: 'Sélection Essentielle', cible: 0.22,
      promesse: 'Le meilleur accord possible dans une gamme accessible.' },
    { id: 'signature', nom: 'Sélection Signature',   cible: 0.55,
      promesse: 'Un accord plus précis, le meilleur équilibre plaisir-prix.' },
    { id: 'exception', nom: 'Sélection Exception',   cible: 0.90,
      promesse: 'Le choix du caviste, pour un accord ou un moment rare.' }
  ];

  /* -------------------------------------------------------------------
     ÉTIQUETTES DE L'ÉVENTAIL
     La rangée de bouteilles d'un plat est TOUJOURS rangée du prix le
     plus doux au plus élevé (`Moteur.eventail` la trie ainsi avant de
     la rendre). Ces quatre mots disent cette échelle de budget d'un
     coup d'œil, pour qu'on n'ait pas à comparer les prix un à un.
     Ils ne nomment jamais le client — on ne décrète pas qui est
     « connaisseur » —, ils nomment la place de la bouteille.
     Quand une rangée compte moins de quatre propositions (réglage
     « Bouteilles proposées par plat »), on prend les échelons les plus
     éloignés : le client doit lire un ÉVENTAIL, pas quatre nuances.
     ------------------------------------------------------------------- */
  S.etiquettes_budget = ['Prix juste', 'Bel équilibre', 'Belle bouteille', 'Exception'];

  /* Budgets indicatifs par personne et par repas, en euros. Servent à
     pré-régler le curseur ; le client peut toujours saisir son montant. */
  S.budgets = [
    { id: 'economique', nom: 'Économique', par_personne: 8,  gamme: 'essentiel' },
    { id: 'equilibre',  nom: 'Équilibré',  par_personne: 15, gamme: 'signature' },
    { id: 'plaisir',    nom: 'Plaisir',    par_personne: 30, gamme: 'exception' },
    { id: 'sur_mesure', nom: 'Sur mesure', par_personne: 0,  gamme: 'signature' }
  ];

  /* =====================================================================
     LES FAMILLES DE BOISSON
     ---------------------------------------------------------------------
     `detecte` : mots recherchés dans la catégorie/le type/l'appellation
     du produit. L'ordre de la liste compte : le moteur retient la
     PREMIÈRE famille qui reconnaît le produit. Les cas particuliers
     (sans alcool, effervescents) passent donc avant les cas généraux.

     `puissance` : place la famille sur l'échelle de progression du repas.
     `verre` : contenance d'un verre servi, en cl — c'est ce qui fait la
     différence entre une bouteille de vin (6 verres) et une bouteille de
     whisky (17 verres). Sans cela, on proposerait des quantités
     absurdes.
     `alcool` : false pour tout le parcours sans alcool.
     ===================================================================== */
  S.familles = [
    /* ---------------------------------------------------------------
       L'ORDRE EST LA RÈGLE : la PREMIÈRE famille reconnue l'emporte.
       On va donc du nom le plus distinctif au plus générique.

       ⚠️ « blanc », « rouge », « rosé » et « champagne » ne sont PAS
       des noms de familles : ce sont des adjectifs et un lieu, qui
       apparaissent au milieu de noms de spiritueux. Testés trop tôt,
       ils rangeaient huit rhums blancs agricoles parmi les VINS BLANCS
       SECS — proposables sur des huîtres — et quatre cognacs de Fine
       ou Petite Champagne parmi les CHAMPAGNES. D'où l'ordre :
         1. sans alcool  (« gin sans alcool » n'est pas un gin)
         2. spiritueux   (noms propres, sans ambiguïté)
         3. bières
         4. bulles       (après les cognacs de « Fine Champagne »)
         5. vins         (les adjectifs de couleur, en dernier)
       Au sein des spiritueux, l'eau-de-vie passe avant son arrangement :
       sans quoi « Armagnac arrangé » devenait un punch.
       --------------------------------------------------------------- */

    /* ---- 1. Sans alcool ---- */
    { id: 'sans_alcool_spiritueux', nom: 'Spiritueux sans alcool', groupe: 'sans_alcool', alcool: false,
      detecte: ['sans alcool', 'sans-alcool', 'zero alcool', 'alcohol free'], puissance: 1, verre: 8 },
    { id: 'tonic', nom: 'Tonic & mixers', groupe: 'sans_alcool', alcool: false,
      detecte: ['tonic', 'ginger beer', 'soda'], puissance: 0, verre: 20 },
    { id: 'jus', nom: 'Jus & pétillants sans alcool', groupe: 'sans_alcool', alcool: false,
      detecte: ['jus de', 'jus ', 'nectar', 'petillant sans', 'kombucha'], puissance: 0, verre: 20 },
    { id: 'the', nom: 'Thés & infusions', groupe: 'sans_alcool', alcool: false,
      detecte: ['the', 'thes', 'infusion', 'rooibos'], puissance: 0, verre: 20 },

    /* ---- 2. Spiritueux — noms propres, aucune ambiguïté ---- */
    { id: 'whisky', nom: 'Whisky', groupe: 'spiritueux', alcool: true,
      detecte: ['whisky', 'whiskey', 'single malt', 'bourbon', 'scotch'], puissance: 3, verre: 4 },
    { id: 'cognac', nom: 'Cognac', groupe: 'spiritueux', alcool: true,
      detecte: ['cognac'], puissance: 3, verre: 4 },
    { id: 'armagnac', nom: 'Armagnac', groupe: 'spiritueux', alcool: true,
      detecte: ['armagnac', 'bas-armagnac'], puissance: 3, verre: 4 },
    { id: 'calvados', nom: 'Calvados', groupe: 'spiritueux', alcool: true,
      detecte: ['calvados'], puissance: 3, verre: 4 },
    { id: 'rhum', nom: 'Rhum', groupe: 'spiritueux', alcool: true,
      detecte: ['rhum', 'ron ', 'rum'], puissance: 3, verre: 4 },
    { id: 'punch', nom: 'Punch & rhum arrangé', groupe: 'spiritueux', alcool: true,
      detecte: ['punch', 'arrange', 'arrangé'], puissance: 2.4, verre: 6 },
    { id: 'gin', nom: 'Gin', groupe: 'spiritueux', alcool: true,
      detecte: ['gin'], puissance: 2.8, verre: 5 },
    { id: 'vodka', nom: 'Vodka', groupe: 'spiritueux', alcool: true,
      detecte: ['vodka'], puissance: 2.8, verre: 5 },
    { id: 'tequila', nom: 'Tequila & mezcal', groupe: 'spiritueux', alcool: true,
      detecte: ['tequila', 'téquila', 'mezcal'], puissance: 2.9, verre: 5 },
    { id: 'pastis', nom: 'Pastis & anisés', groupe: 'spiritueux', alcool: true,
      detecte: ['pastis', 'anis'], puissance: 2, verre: 3 },
    { id: 'liqueur', nom: 'Liqueur & crème', groupe: 'spiritueux', alcool: true,
      detecte: ['liqueur', 'creme de', 'crème de'], puissance: 2.2, verre: 5 },
    { id: 'eau_de_vie', nom: 'Eau-de-vie', groupe: 'spiritueux', alcool: true,
      detecte: ['eau de vie', 'eaux de vie', 'marc', 'grappa', 'kirsch'], puissance: 3, verre: 3 },

    /* ---- 3. Bières ---- */
    { id: 'biere', nom: 'Bière', groupe: 'biere', alcool: true,
      detecte: ['biere', 'bière', 'ipa', 'lager', 'stout', 'blonde de', 'triple'], puissance: 1.3, verre: 25 },

    /* ---- 4. Bulles ---- */
    { id: 'champagne', nom: 'Champagne', groupe: 'bulles', alcool: true,
      detecte: ['champagne'], puissance: 1.6, verre: 10 },
    { id: 'effervescent', nom: 'Effervescent', groupe: 'bulles', alcool: true,
      detecte: ['cremant', 'crémant', 'prosecco', 'petillant naturel', 'pet-nat', 'effervescent', 'mousseux', 'blanquette', 'clairette'],
      puissance: 1.4, verre: 10 },

    /* ---- 5. Vins tranquilles. Les sous-familles blanches et rouges
            sont départagées plus loin par la puissance lue dans les
            tags. Les adjectifs de couleur ferment la marche. ---- */
    { id: 'liquoreux', nom: 'Blanc liquoreux', groupe: 'blanc', alcool: true,
      detecte: ['liquoreux', 'monbazillac', 'sauternes', 'saussignac', 'jurancon moelleux'], puissance: 2.2, verre: 7 },
    { id: 'moelleux', nom: 'Blanc moelleux', groupe: 'blanc', alcool: true,
      detecte: ['moelleux', 'demi-sec', 'demi sec'], puissance: 1.8, verre: 8 },
    { id: 'vdn', nom: 'Vin doux naturel', groupe: 'blanc', alcool: true,
      detecte: ['vin doux naturel', 'porto', 'banyuls', 'maury', 'rasteau', 'muscat de'], puissance: 2.4, verre: 7 },
    { id: 'blanc_sec', nom: 'Blanc sec', groupe: 'blanc', alcool: true,
      detecte: ['vin blanc', 'blanc sec', 'blanc'], puissance: 1.2, verre: 12.5 },
    { id: 'rose', nom: 'Rosé', groupe: 'rose', alcool: true,
      detecte: ['vin rose', 'rose', 'rosé', 'gris'], puissance: 1.1, verre: 12.5 },
    { id: 'rouge', nom: 'Rouge', groupe: 'rouge', alcool: true,
      detecte: ['vin rouge', 'rouge'], puissance: 2.0, verre: 12.5 }
  ];

  /* -------------------------------------------------------------------
     COULEURS — le filtre que le client actionne lui-même
     ---------------------------------------------------------------------
     Contrairement aux familles ci-dessus, qui sont DÉDUITES pour les
     besoins de l'accord, les couleurs sont lues dans le métachamp
     « catégorie » de la fiche, celui que la cave remplit à la main :
     « Vin Rouge », « Vin Blanc Demi-Sec », « Vin Blanc Liquoreux »… Le
     client qui coche « rouge » filtre donc sur ce que la maison a
     réellement écrit, pas sur une devinette du moteur.

     L'ORDRE COMPTE, du plus précis au plus général :
       — « Champagne Rosé » est une bulle, pas un rosé : les bulles
         passent donc en premier ;
       — « Vin Blanc Demi-Sec » doit être testé avant « blanc sec »,
         et les moelleux avant, sans quoi tout finirait en blanc sec.
     ------------------------------------------------------------------- */
  S.couleurs = [
    { id: 'bulles', nom: 'Bulles', groupe: 'bulles',
      detecte: ['champagne', 'cremant', 'prosecco', 'petillant', 'mousseux', 'blanquette', 'clairette', 'brut'] },
    { id: 'demi_sec', nom: 'Blanc demi-sec',
      detecte: ['blanc demi sec', 'demi sec'] },
    { id: 'moelleux', nom: 'Moelleux & liquoreux',
      detecte: ['liquoreux', 'moelleux', 'vin doux naturel', 'monbazillac', 'sauternes', 'saussignac'] },
    { id: 'blanc_sec', nom: 'Blanc sec',
      detecte: ['blanc sec', 'vin blanc'] },
    { id: 'rose', nom: 'Rosé', groupe: 'rose',
      detecte: ['vin rose', 'rose', 'gris'] },
    { id: 'rouge', nom: 'Rouge', groupe: 'rouge',
      detecte: ['vin rouge', 'rouge'] }
  ];

  /* -------------------------------------------------------------------
     CATÉGORIES — l'autre filtre facultatif
     Regroupe les familles en rayons tels que le client les nomme. Un
     rayon ne sert à rien s'il est vide : l'interface n'affiche que ceux
     qui ont réellement des bouteilles achetables aujourd'hui.
     ------------------------------------------------------------------- */
  S.categories = [
    { id: 'vin',        nom: 'Vin',                  groupes: ['rouge', 'rose', 'blanc'] },
    { id: 'bulles',     nom: 'Champagne & bulles',   groupes: ['bulles'] },
    { id: 'biere',      nom: 'Bière',                familles: ['biere'] },
    { id: 'whisky',     nom: 'Whisky',               familles: ['whisky'] },
    { id: 'rhum',       nom: 'Rhum',                 familles: ['rhum', 'punch'] },
    { id: 'eau_de_vie', nom: 'Cognac, armagnac & calvados', familles: ['cognac', 'armagnac', 'calvados', 'eau_de_vie'] },
    { id: 'blanc_spi',  nom: 'Gin, vodka & tequila', familles: ['gin', 'vodka', 'tequila'] },
    { id: 'liqueur',    nom: 'Liqueurs',             familles: ['liqueur'] },
    { id: 'soft',       nom: 'Sans alcool',          groupes: ['sans_alcool'] }
  ];

  /* Sous-familles de vin, départagées par les mots de style présents
     dans les étiquettes du produit. Elles n'existent pas dans le
     catalogue : c'est le moteur qui les déduit, et c'est volontaire —
     un vin est d'abord « rouge », son poids se lit dans ses mots. */
  S.styles = {
    /* Un rouge léger et un rouge de garde n'ont rien à faire au même
       moment du repas. */
    rouge_leger:   ['leger', 'fruite', 'gouleyant', 'souple', 'gamay', 'pinot noir', 'primeur', 'soyeux', 'tanins soyeux', 'tanins fondus', 'frais'],
    rouge_puissant:['puissant', 'structure', 'tanique', 'corse', 'de garde', 'concentre', 'intense', 'boise', 'madiran', 'cahors', 'syrah', 'tannat', 'vieilles vignes', 'gastronomique'],
    blanc_vif:     ['vif', 'tendu', 'mineral', 'sec', 'agrume', 'iode', 'sauvignon', 'muscadet', 'chablis', 'riesling', 'aligote', 'tension', 'fraicheur'],
    blanc_riche:   ['boise', 'gras', 'ample', 'rond', 'chardonnay', 'fut', 'beurre', 'brioche', 'vanille', 'opulent', 'genereux'],
    rose_vif:      ['vif', 'sec', 'pale', 'provence', 'agrume'],
    biere_forte:   ['ipa', 'stout', 'triple', 'brune', 'ambree']
  };

  /* Signaux « bio / nature », lus dans les étiquettes du produit. */
  S.mots_bio = ['bio', 'biologique', 'biodynamie', 'biodynamique', 'nature', 'sans sulfite', 'hve'];

  /* =====================================================================
     LE PROFIL ORGANOLEPTIQUE DE LA BOUTEILLE  (v6.0)
     ---------------------------------------------------------------------
     Jusqu'ici le moteur ne connaissait d'une bouteille que sa FAMILLE
     (« rouge », « blanc sec ») et quelques mots de style lus dans ses
     étiquettes. C'est ce qui suffit pour dire « du rouge sur une côte de
     bœuf » — et ce qui ne suffit pas pour faire le travail d'un
     sommelier : entre deux rouges, c'est la STRUCTURE qui décide, et
     entre deux blancs aussi.

     On note donc chaque bouteille sur sept axes, de 0 à 4 :

       sucre ....... sec 0 · demi-sec 1,5 · moelleux 2,5 · liquoreux 3,5
       acidite ..... la fraîcheur, ce qui « nettoie » la bouche du gras
       tanin ....... l'astringence, ce qui heurte l'iode et l'umami
       corps ....... le poids en bouche, à mettre en face de la puissance du plat
       alcool ...... la chaleur, ce que le piment amplifie
       boise ....... le fût, qui écrase les chairs fines et épouse la fumée
       bulle ....... l'effervescence, qui coupe le gras et la friture

     D'OÙ VIENNENT CES NOTES — et pourquoi elles vaudront aussi pour les
     bouteilles qui entreront demain à la cave : elles ne sont écrites
     nulle part à la main. Elles se DÉDUISENT de ce que la cave remplit
     déjà sur chaque fiche : la catégorie, les cépages (181 fiches sur
     193 les portent), le degré, le taux de sucre, l'appellation, et les
     mots d'élevage de la note de dégustation. Une fiche complétée
     demain donne son profil toute seule.
     ===================================================================== */

  /* Profil de départ, par famille. C'est le socle : les cépages et
     l'appellation viennent ensuite le corriger. */
  S.profil_familles = {
    blanc_sec:    { sucre: 0.2, acidite: 3.0, tanin: 0.1, corps: 1.8, alcool: 1.8, boise: 0.3, bulle: 0 },
    moelleux:     { sucre: 2.5, acidite: 2.6, tanin: 0.1, corps: 2.4, alcool: 1.8, boise: 0.4, bulle: 0 },
    liquoreux:    { sucre: 3.6, acidite: 2.8, tanin: 0.1, corps: 3.2, alcool: 2.2, boise: 1.0, bulle: 0 },
    vdn:          { sucre: 3.4, acidite: 2.0, tanin: 0.8, corps: 3.4, alcool: 3.2, boise: 1.4, bulle: 0 },
    rose:         { sucre: 0.3, acidite: 2.8, tanin: 0.5, corps: 1.6, alcool: 1.8, boise: 0.1, bulle: 0 },
    rouge:        { sucre: 0.1, acidite: 2.0, tanin: 2.4, corps: 2.6, alcool: 2.2, boise: 0.8, bulle: 0 },
    champagne:    { sucre: 0.8, acidite: 3.4, tanin: 0.1, corps: 1.8, alcool: 1.8, boise: 0.3, bulle: 3.4 },
    effervescent: { sucre: 0.9, acidite: 3.2, tanin: 0.1, corps: 1.5, alcool: 1.5, boise: 0.1, bulle: 3.2 },
    biere:        { sucre: 0.4, acidite: 1.2, tanin: 0.3, corps: 1.8, alcool: 1.2, boise: 0.1, bulle: 2.6, amertume: 2.2, torrefaction: 0.4 },
    /* Les spiritueux ne se jugent pas sur les mêmes axes ; on leur donne
       un profil cohérent pour que les lois ne les traitent pas comme des
       vins mous. Leur place à table est de toute façon verrouillée
       ailleurs. */
    whisky:  { sucre: 0.4, acidite: 0.5, tanin: 0.5, corps: 3.6, alcool: 4, boise: 3, bulle: 0 },
    cognac:  { sucre: 0.8, acidite: 0.5, tanin: 0.4, corps: 3.6, alcool: 4, boise: 3, bulle: 0 },
    armagnac:{ sucre: 0.8, acidite: 0.5, tanin: 0.4, corps: 3.7, alcool: 4, boise: 3, bulle: 0 },
    calvados:{ sucre: 0.5, acidite: 1.2, tanin: 0.3, corps: 3.4, alcool: 4, boise: 2.4, bulle: 0 },
    rhum:    { sucre: 1.2, acidite: 0.4, tanin: 0.3, corps: 3.5, alcool: 4, boise: 2.4, bulle: 0 },
    punch:   { sucre: 2.6, acidite: 0.8, tanin: 0.2, corps: 2.6, alcool: 3, boise: 0.8, bulle: 0 },
    gin:     { sucre: 0.2, acidite: 0.5, tanin: 0.2, corps: 2.6, alcool: 3.8, boise: 0.3, bulle: 0 },
    vodka:   { sucre: 0.1, acidite: 0.3, tanin: 0.1, corps: 2.2, alcool: 3.8, boise: 0.1, bulle: 0 },
    tequila: { sucre: 0.3, acidite: 0.8, tanin: 0.2, corps: 2.8, alcool: 3.8, boise: 1.2, bulle: 0 },
    liqueur: { sucre: 3.4, acidite: 0.8, tanin: 0.2, corps: 2.6, alcool: 2.6, boise: 0.6, bulle: 0 },
    pastis:  { sucre: 1.2, acidite: 0.3, tanin: 0.1, corps: 2.2, alcool: 3.6, boise: 0.1, bulle: 0 },
    eau_de_vie:{ sucre: 0.2, acidite: 0.6, tanin: 0.2, corps: 3.2, alcool: 4, boise: 1.2, bulle: 0 },
    tonic:   { sucre: 1.6, acidite: 2.0, tanin: 0.4, corps: 0.6, alcool: 0, boise: 0, bulle: 3 },
    jus:     { sucre: 2.4, acidite: 2.2, tanin: 0.2, corps: 1.2, alcool: 0, boise: 0, bulle: 0.6 },
    the:     { sucre: 0.2, acidite: 1.0, tanin: 1.6, corps: 1.0, alcool: 0, boise: 0, bulle: 0 },
    sans_alcool_spiritueux: { sucre: 1.2, acidite: 1.6, tanin: 0.4, corps: 1.4, alcool: 0, boise: 0.2, bulle: 0 }
  };

  /* ---------------------------------------------------------------------
     LES CÉPAGES — la connaissance qui vaut pour toutes les bouteilles à
     venir. Chaque cépage reconnu dans le champ « cépages » de la fiche
     tire le profil vers ses valeurs propres ; on fait la moyenne quand il
     y en a plusieurs, ce qui est le cas de presque tous les assemblages.
     Les valeurs sont celles qu'un sommelier annoncerait à l'aveugle.
     --------------------------------------------------------------------- */
  S.cepages = [
    /* --- Blancs --- */
    { mots: ['sauvignon blanc', 'sauvignon gris', 'sauvignon'], profil: { acidite: 3.6, corps: 1.6, aromatique: 3, boise: 0 } },
    { mots: ['chardonnay'],        profil: { acidite: 2.6, corps: 2.6, aromatique: 1.6, boise: 1 } },
    { mots: ['semillon', 'sémillon'], profil: { acidite: 2.2, corps: 3.0, aromatique: 1.4, boise: 0.6 } },
    { mots: ['muscadelle'],        profil: { acidite: 2.4, corps: 2.0, aromatique: 2.8 } },
    { mots: ['riesling'],          profil: { acidite: 3.9, corps: 1.8, aromatique: 2.6, boise: 0 } },
    { mots: ['chenin'],            profil: { acidite: 3.4, corps: 2.4, aromatique: 2.2 } },
    { mots: ['viognier'],          profil: { acidite: 1.8, corps: 3.2, aromatique: 3.4 } },
    { mots: ['gewurztraminer', 'gewurtztraminer'], profil: { acidite: 1.6, corps: 3.2, aromatique: 3.8 } },
    { mots: ['muscat'],            profil: { acidite: 2.0, corps: 2.4, aromatique: 3.6 } },
    { mots: ['gros manseng', 'petit manseng', 'manseng'], profil: { acidite: 3.4, corps: 2.6, aromatique: 2.8 } },
    { mots: ['colombard', 'colombart', 'ugni blanc', 'ugny blanc'], profil: { acidite: 3.4, corps: 1.2, aromatique: 1.6 } },
    { mots: ['pinot blanc', 'pinot gris'], profil: { acidite: 2.4, corps: 2.2, aromatique: 1.8 } },
    { mots: ['grenache blanc', 'grenache gris', 'macabeu', 'roussanne', 'marsanne'], profil: { acidite: 2.0, corps: 3.0, aromatique: 1.8 } },
    { mots: ['aligote', 'aligoté', 'melon de bourgogne', 'muscadet'], profil: { acidite: 3.6, corps: 1.4, aromatique: 1.2 } },
    { mots: ['glera'],             profil: { acidite: 2.8, corps: 1.2, aromatique: 2.2 } },
    { mots: ['ondenc', 'len de l el', 'mauzac'], profil: { acidite: 3.0, corps: 1.8, aromatique: 2.0 } },
    /* --- Rouges --- */
    { mots: ['merlot'],            profil: { tanin: 2.0, corps: 2.6, acidite: 1.8, aromatique: 1.8 } },
    { mots: ['cabernet sauvignon'],profil: { tanin: 3.4, corps: 3.0, acidite: 2.2, boise: 1.2 } },
    { mots: ['cabernet franc', 'cabernet-franc'], profil: { tanin: 2.6, corps: 2.4, acidite: 2.4, aromatique: 2.2 } },
    { mots: ['malbec', 'cot ', 'côt', 'auxerrois'], profil: { tanin: 3.4, corps: 3.4, acidite: 2.0, boise: 1.2 } },
    { mots: ['tannat'],            profil: { tanin: 4.0, corps: 3.6, acidite: 2.2, boise: 1.4 } },
    { mots: ['syrah', 'shiraz'],   profil: { tanin: 3.0, corps: 3.4, acidite: 2.0, aromatique: 2.4, boise: 1.2 } },
    { mots: ['grenache noir', 'grenache'], profil: { tanin: 2.0, corps: 3.2, acidite: 1.6, alcool: 3.0 } },
    { mots: ['mourvedre', 'mourvèdre'], profil: { tanin: 3.2, corps: 3.2, acidite: 2.0 } },
    { mots: ['carignan'],          profil: { tanin: 2.8, corps: 2.8, acidite: 2.4 } },
    { mots: ['cinsault'],          profil: { tanin: 1.4, corps: 1.8, acidite: 2.4 } },
    { mots: ['pinot noir'],        profil: { tanin: 1.6, corps: 2.0, acidite: 2.8, aromatique: 2.4 } },
    { mots: ['pinot meunier'],     profil: { tanin: 1.6, corps: 2.2, acidite: 2.6 } },
    { mots: ['gamay'],             profil: { tanin: 1.2, corps: 1.6, acidite: 2.8, aromatique: 2.2 } },
    { mots: ['petit verdot'],      profil: { tanin: 3.6, corps: 3.2, acidite: 2.2, boise: 1.2 } },
    { mots: ['fer servadou', 'braucol', 'negrette', 'négrette'], profil: { tanin: 2.8, corps: 2.6, acidite: 2.2, aromatique: 2.4 } },
    { mots: ['carmenere', 'carmenère'], profil: { tanin: 2.8, corps: 3.0, acidite: 1.8 } },
    { mots: ['barbera'],           profil: { tanin: 2.0, corps: 2.4, acidite: 3.4 } },
    { mots: ['nebbiolo'],          profil: { tanin: 3.8, corps: 3.0, acidite: 3.2 } },
    { mots: ['sangiovese'],        profil: { tanin: 2.8, corps: 2.6, acidite: 3.2 } },
    { mots: ['tempranillo'],       profil: { tanin: 2.6, corps: 2.8, acidite: 2.2, boise: 1.4 } }
  ];

  /* =====================================================================
     LES EFFERVESCENTS NE SONT PAS INTERCHANGEABLES  (v6.1)
     ---------------------------------------------------------------------
     Un Blanc de Blancs extra-brut, un champagne vineux millésimé, un
     crémant et un demi-sec à 32 g/l n'ont pas le même usage à table. Le
     moteur les traitait pourtant comme une seule famille « champagne ».
     Tout ce qu'il faut est déjà sur les fiches : la cave écrit le dosage
     en g/l sur 31 effervescents sur 33, et le style dans la catégorie.
     Le dosage prime ; ces mots ne servent que lorsqu'il manque.
     ===================================================================== */
  S.dosages = [
    { mots: ['brut nature', 'non dose', 'zero dosage', 'pas dose'], g: 1 },
    { mots: ['extra brut', 'extra-brut'],                            g: 4 },
    { mots: ['extra dry'],                                           g: 15 },
    { mots: ['demi sec', 'demi-sec'],                                g: 33 },
    { mots: ['doux'],                                                g: 50 },
    { mots: ['brut'],                                                g: 8 }
  ];
  S.styles_effervescent = [
    /* Le chardonnay seul : plus de tension, moins de corps. C'est le vin
       des huîtres et du caviar. */
    { mots: ['blanc de blancs', 'blanc de blanc'], profil: { acidite: 3.8, corps: 1.6, aromatique: 1.8 } },
    /* Les raisins noirs : plus de chair, plus de vinosité. */
    { mots: ['blanc de noirs', 'blanc de noir'],   profil: { acidite: 2.8, corps: 2.8 } },
    { mots: ['rose', 'rosé'],                       profil: { acidite: 2.8, corps: 2.4, aromatique: 2.6 } },
    /* Un millésimé ou une cuvée de prestige a vieilli sur lattes : il
       gagne le corps et les notes briochées qui lui permettent de tenir
       un plat, là où un brut sans année reste un vin d'apéritif. */
    { mots: ['millesime', 'grande annee', 'prestige', 'solera', 'grand cru'],
      profil: { corps: 3, boise: 1.4, aromatique: 2.6 } },
    { mots: ['prosecco', 'glera'],                  profil: { acidite: 2.6, corps: 1.2, aromatique: 2.4, bulle: 2.6 } },
    { mots: ['cremant', 'crémant', 'petillant naturel', 'pet nat'],
      profil: { acidite: 3, corps: 1.6, bulle: 2.9 } }
  ];

  /* =====================================================================
     LES BIÈRES NE SONT PAS UNE SEULE FAMILLE  (v6.1)
     ---------------------------------------------------------------------
     Le moteur ne connaissait qu'une famille « bière » et un style
     « bière forte ». Les douze bières du rayon — NEIPA, Double IPA,
     Imperial Stout, Coffee Stout, Sour, Mexican Lager — étaient donc
     traitées presque identiquement, alors qu'une stout impériale à 12°
     et une lager mexicaine à 4,5° n'ont rien à faire dans la même
     assiette. Le style est écrit dans la catégorie de chaque fiche : on
     le lit, on n'invente rien.

     Deux axes propres à la bière viennent s'ajouter au profil :
       amertume ..... le houblon, qui nettoie le gras mais AMPLIFIE le piment
       torrefaction . le malt brûlé, qui répond au grillé et au cacao
     ===================================================================== */
  S.styles_biere = [
    { mots: ['imperial stout', 'russian imperial'],
      profil: { amertume: 3.2, torrefaction: 4, corps: 4, sucre: 1.6, alcool: 3.4, acidite: 0.8 } },
    { mots: ['coffee stout', 'stout', 'porter'],
      profil: { amertume: 3, torrefaction: 3.6, corps: 3.2, sucre: 1.2, alcool: 2.4, acidite: 0.8 } },
    { mots: ['double ipa', 'dipa', 'triple ipa'],
      profil: { amertume: 3.6, torrefaction: 0.4, corps: 2.8, aromatique: 3.6, alcool: 3, acidite: 1.2 } },
    /* La NEIPA est trouble et juteuse : beaucoup d'arôme, peu d'amertume.
       La confondre avec une IPA classique est l'erreur la plus courante. */
    { mots: ['ddh neipa', 'neipa', 'new england ipa', 'hazy'],
      profil: { amertume: 1.8, torrefaction: 0.2, corps: 2.6, aromatique: 4, alcool: 2.2, acidite: 1.4 } },
    { mots: ['ipa', 'india pale ale'],
      profil: { amertume: 3.4, torrefaction: 0.3, corps: 2.4, aromatique: 3.4, alcool: 2.4, acidite: 1.2 } },
    { mots: ['fruited sour', 'sour ale', 'sour', 'gose', 'berliner', 'lambic', 'gueuze'],
      profil: { amertume: 0.6, torrefaction: 0, corps: 1.6, acidite: 3.8, sucre: 1.4, aromatique: 3.2, alcool: 1.6 } },
    { mots: ['mexican lager', 'lager', 'pils', 'pilsner', 'helles'],
      profil: { amertume: 1.8, torrefaction: 0.2, corps: 1.4, acidite: 1.2, aromatique: 1.4, alcool: 1.4 } },
    { mots: ['blanche', 'witbier', 'weizen', 'weiss'],
      profil: { amertume: 1, torrefaction: 0, corps: 1.8, acidite: 2, aromatique: 3, alcool: 1.6 } },
    { mots: ['triple', 'tripel', 'blonde forte', 'abbaye'],
      profil: { amertume: 2.2, torrefaction: 0.4, corps: 3, sucre: 1.2, aromatique: 2.8, alcool: 3.2 } },
    { mots: ['ambree', 'ambrée', 'amber', 'brune', 'dubbel'],
      profil: { amertume: 1.8, torrefaction: 2.2, corps: 2.6, sucre: 1.4, aromatique: 2.2, alcool: 2.2 } },
    { mots: ['blonde', 'pale ale', 'ale'],
      profil: { amertume: 2.2, torrefaction: 0.4, corps: 2, aromatique: 2.2, alcool: 1.8 } }
  ];

  /* =====================================================================
     LE RANG DE L'APPELLATION  (v6.3)
     ---------------------------------------------------------------------
     Ce qui manquait pour que le conseil soit celui d'un caviste et non
     d'un tableur. Le moteur savait qu'un vin était « blanc ample » ; il
     ne savait pas ce que la bouteille VAUT. Un Chardonnay Vin de France
     à 5,90 € et un Bourgogne blanc sont tous deux des blancs amples —
     alors le moteur prenait le moins cher, et conseillait le vin le plus
     modeste de la cave sur une truffe, un plateau de fromages, une
     bouillabaisse et un risotto aux cèpes.

     Un sommelier ne fait jamais ça. Sur un plat de fête, il monte d'un
     cran. On note donc chaque bouteille sur l'échelle des appellations :

       1  vin de France, IGP, vin de pays          le quotidien
       2  appellation régionale (Bordeaux, Bergerac)
       3  appellation communale (Pécharmant, Fronsac, Chablis)
       4  cru et grande appellation (Pomerol, Sauternes, Grand Cru)

     Ce rang ne remplace pas l'accord — un mauvais accord en cru reste un
     mauvais accord. Il départage deux vins également justes, et il
     empêche de conseiller un vin de soif sur un plat de fête.
     ⚠️ Ce N'EST PAS le prix : un Pécharmant à 14 € est de rang 3, une
     cuvée sans appellation à 40 € reste de rang 1. C'est l'appellation
     qui parle, pas l'étiquette de prix.
     ===================================================================== */
  S.rangs_appellation = [
    /* Rang 4 — les crus. L'ordre compte : « Saint-Émilion Grand Cru »
       doit être lu avant « Saint-Émilion ». */
    { rang: 4, mots: ['grand cru', 'premier cru', 'pomerol', 'lalande de pomerol', 'sauternes',
                      'pauillac', 'saint estephe', 'saint julien', 'margaux', 'pessac leognan',
                      'monbazillac', 'saussignac', 'barsac', 'chateauneuf', 'hermitage',
                      'cote rotie', 'meursault', 'chablis grand cru', 'pouilly fume',
                      'barolo', 'barbaresco', 'brunello'] },
    /* Rang 3 — les appellations communales et les grands terroirs. */
    { rang: 3, mots: ['pecharmant', 'fronsac', 'montravel', 'cahors', 'madiran', 'saint emilion',
                      'medoc', 'graves', 'castillon', 'sainte foy', 'cotes de bourg', 'blaye',
                      'saumur', 'chinon', 'sancerre', 'chablis', 'meursault', 'volnay',
                      'gigondas', 'vacqueyras', 'bandol', 'terrasses du larzac', 'pic saint loup',
                      'maury', 'banyuls', 'rivesaltes', 'rasteau', 'jurancon', 'rosette',
                      'barbera', 'chianti', 'champagne'] },
    /* Rang 2 — les appellations régionales. */
    { rang: 2, mots: ['bordeaux', 'bergerac', 'bourgogne', 'cotes du rhone', 'cotes de provence',
                      'cotes du roussillon', 'languedoc', 'alsace', 'anjou', 'touraine',
                      'cotes de gascogne', 'perigord', 'cremant', 'prosecco'] }
    /* Tout le reste — vin de France, IGP, vin de pays — reste au rang 1. */
  ];

  /* Le rang ATTENDU selon le registre du plat. Un plat de tous les jours
     ne réclame rien ; un plat de fête réclame qu'on monte d'un cran. */
  S.rang_attendu = { 1: 1, 2: 2, 3: 3 };

  /* Mots d'ÉLEVAGE lus dans la note de dégustation et les étiquettes :
     le bois ne se devine pas au cépage, il se lit dans le texte. */
  S.mots_boise = ['fut', 'futs', 'barrique', 'chene', 'eleve en fut', 'boise', 'vanille', 'toaste', 'torrefie'];
  /* Un vin doux MUTÉ (vin doux naturel, porto) et un vin doux BOTRYTISÉ
     (sauternes, monbazillac) ne se servent pas sur les mêmes desserts :
     le chocolat veut le premier, la tarte aux fruits le second. */
  S.familles_mutees = ['vdn'];
  S.familles_botrytis = ['liquoreux', 'moelleux'];

  /* =====================================================================
     LES LOIS DE L'ACCORD  (v6.0)
     ---------------------------------------------------------------------
     Ce qui suit n'est pas une liste de plats : ce sont les règles que
     tout sommelier applique sur une assiette qu'il découvre. Elles
     comparent les axes du PLAT (gras, iode, umami, piment, sucre…) au
     profil de la BOUTEILLE. C'est ce qui permet de répondre juste sur un
     plat que personne n'a jamais écrit dans la bibliothèque — et c'est ce
     qui empêche un bon score de famille de couvrir une faute de
     structure.

     `si` lit les axes du plat, `effet` renvoie un nombre de points à
     ajouter (négatif pour un malus). `veto` renvoie true quand l'accord
     est une faute franche, quel que soit le reste.
     ===================================================================== */
  /* =====================================================================
     LES NIVEAUX D'ACCORD  (v6.1)
     ---------------------------------------------------------------------
     Le score d'un produit est rapporté à ce que le plat réclame : la
     meilleure note que sa fiche d'archétype accorde à une famille de
     boisson. Un rapport de 1 signifie « exactement le vin que ce plat
     appelle » ; au-dessus, les signaux de la maison (recette, accords
     écrits, terroir) ont confirmé le choix.

     `plancher_rangee` est la règle demandée par Grégoire : en dessous de
     ce niveau, on n'occupe pas la case. Une rangée de deux bouteilles
     justes vaut mieux qu'une rangée de quatre dont une fausse.
     ===================================================================== */
  S.niveaux_accord = [
    { id: 'excellent',  seuil: 1.05, nom: 'Accord d’exception',  court: 'Exceptionnel' },
    { id: 'tres_bon',   seuil: 0.88, nom: 'Très bel accord',     court: 'Très bon' },
    { id: 'bon',        seuil: 0.70, nom: 'Bon accord',          court: 'Bon' },
    { id: 'acceptable', seuil: 0.52, nom: 'Accord correct',      court: 'Correct' }
  ];
  S.plancher_rangee = 'bon';

  S.lois = [
    {
      id: 'iode_tanin',
      nom: 'L’iode et le tanin',
      explication: "Le tanin sur un produit de la mer donne un goût métallique : c’est la faute la plus connue de la table.",
      si: function (a) { return (a.iode || 0) >= 1.8; },
      effet: function (a, v) { return -(v.tanin) * (a.iode) * 5; },
      veto: function (a, v) { return (a.iode || 0) >= 2.5 && v.tanin >= 2.6; }
    },
    {
      id: 'umami_tanin',
      nom: 'L’umami durcit le tanin',
      explication: "Champignon, soja, parmesan, tomate cuite : l’umami fait ressortir l’amertume du tanin et assèche le vin.",
      si: function (a) { return (a.umami || 0) >= 1.8; },
      effet: function (a, v) { return -(v.tanin) * (a.umami) * 2.5; }
    },
    {
      id: 'amer_tanin',
      nom: 'L’amer sur l’amer',
      explication: "Cacao, café, endive, roquette : deux amertumes s’additionnent au lieu de se répondre.",
      si: function (a) { return (a.amer || 0) >= 1.8; },
      effet: function (a, v) { return -(v.tanin) * (a.amer) * 2.5; }
    },
    {
      id: 'piment_alcool',
      nom: 'Le piment et l’alcool',
      explication: "Le piment amplifie la sensation d’alcool et de tanin ; un peu de sucre l’éteint au contraire.",
      si: function (a) { return (a.epice || 0) >= 1.4; },
      effet: function (a, v) {
        var d = 0;
        if (v.alcool > 2.2) d -= (v.alcool - 2.2) * (a.epice) * 8;
        d -= v.tanin * (a.epice) * 3;
        d += Math.min(v.sucre, 2.5) * (a.epice) * 6;
        return d;
      }
    },
    {
      id: 'gras_fraicheur',
      nom: 'Le gras appelle la fraîcheur',
      explication: "Crème, beurre, friture, fromage fondu : il faut au vin de l’acidité ou des bulles pour relancer la bouche.",
      si: function (a) { return (a.gras || 0) >= 1.8; },
      effet: function (a, v) { return (v.acidite * 3.5 + v.bulle * 4) * ((a.gras) / 2.5); }
    },
    {
      id: 'vinaigre_acidite',
      nom: 'L’acide contre l’acide',
      explication: "Vinaigre, citron, marinade : un vin moins acide que le plat paraît mou et plat.",
      si: function (a) { return (a.acide || 0) >= 1.8; },
      effet: function (a, v) { return v.acidite >= 2.6 ? (a.acide) * 5 : -(2.6 - v.acidite) * (a.acide) * 7; }
    },
    {
      id: 'sel_bulle',
      nom: 'Le sel et les bulles',
      explication: "Le sel assèche : les bulles et l’acidité le lavent, un vin épais l’accentue.",
      si: function (a) { return (a.sel || 0) >= 1.8; },
      effet: function (a, v) { return (v.bulle * 3.5 + v.acidite * 2.5) * ((a.sel) / 2.5); }
    },
    {
      id: 'fume_bois',
      nom: 'La fumée et le bois',
      explication: "Une viande fumée ou grillée trouve son écho dans un élevage sous bois et un vin de corps.",
      si: function (a) { return (a.fume || 0) >= 1.8; },
      effet: function (a, v) { return (v.boise * 4 + v.corps * 2) * ((a.fume) / 2.5); }
    },
    {
      id: 'sucre_sur_sucre',
      nom: 'Le vin doit être plus sucré que le dessert',
      explication: "Servi sur un dessert plus sucré que lui, le meilleur vin paraît acide et maigre.",
      si: function (a) { return (a.sucre || 0) >= 2; },
      effet: function (a, v) { return v.sucre >= (a.sucre - 0.6) ? 12 : -(a.sucre - v.sucre) * 22; }
    },
    {
      id: 'sucre_hors_sujet',
      nom: 'Pas de sucre résiduel sur un plat salé',
      explication: "Un demi-sec ou un doux servi sur un plat salé laisse une impression sirupeuse : hors dessert et hors foie gras, le vin se sert sec.",
      /* La règle absolue du moteur ne visait que les FAMILLES douces
         (moelleux, liquoreux, vin doux naturel). Elle laissait passer un
         crémant DEMI-SEC sur du caviar — même famille qu'un brut, et
         pourtant une faute. Ici c'est le sucre réellement mesuré dans le
         verre qui parle, quelle que soit la famille. */
      si: function (a) { return (a.sucre || 0) < 1.2 && !a.veut_doux; },
      effet: function (a, v) { return v.sucre <= 1 ? 0 : -(v.sucre - 1) * 18; }
    },
    {
      id: 'puissance_corps',
      nom: 'Le poids du plat et le poids du vin',
      explication: "Un plat puissant efface un vin léger ; un vin puissant efface un plat délicat.",
      si: function () { return true; },
      effet: function (a, v) {
        var p = a.puissance == null ? 1.5 : a.puissance;
        return -Math.abs(p - v.corps) * 6;
      }
    },
    {
      id: 'amertume_gras',
      nom: 'L’amertume du houblon lave le gras',
      explication: "L’amertume d’une bière houblonnée nettoie la friture et le fromage fondu aussi bien que l’acidité d’un vin.",
      si: function (a) { return (a.gras || 0) >= 2; },
      effet: function (a, v) { return (v.amertume || 0) * 4 * ((a.gras) / 2.5); }
    },
    {
      id: 'amertume_piment',
      nom: 'L’amertume amplifie le piment',
      explication: "Sur un plat qui brûle, une bière très amère en rajoute : c’est la douceur du malt qu’il faut, pas le houblon.",
      /* L'erreur classique du « une IPA sur un curry ». Une IPA très
         amère et alcoolisée décuple la sensation de brûlure ; une NEIPA
         juteuse ou une lager la calment. Le moteur ne pouvait pas faire
         la différence : les deux étaient « bière ». */
      si: function (a) { return (a.epice || 0) >= 1.4; },
      effet: function (a, v) { return -(v.amertume || 0) * (a.epice) * 4; }
    },
    {
      id: 'torrefaction_grille',
      nom: 'Le malt torréfié et le grillé',
      explication: "Le malt brûlé d’une stout prolonge la croûte grillée, la fumée et le cacao — c’est un accord de miroir.",
      si: function (a) { return (a.fume || 0) >= 1.5 || ((a.amer || 0) >= 1.8 && (a.sucre || 0) >= 2); },
      effet: function (a, v) { return (v.torrefaction || 0) * 5; }
    },
    {
      id: 'torrefaction_delicat',
      nom: 'Le torréfié écrase les chairs fines',
      explication: "Une bière noire sur un poisson ou un coquillage : le malt brûlé couvre tout.",
      si: function (a) { return (a.iode || 0) >= 1.8 && (a.fume || 0) < 1.5; },
      effet: function (a, v) { return -(v.torrefaction || 0) * (a.iode) * 3; }
    },
    {
      id: 'chocolat_mute',
      nom: 'Le chocolat veut un vin muté, pas un liquoreux',
      explication: "Sur le cacao, un sauternes ou un monbazillac tourne au sirop : c’est le rancio d’un banyuls, d’un maury ou d’un rivesaltes qu’il faut.",
      si: function (a) { return (a.amer || 0) >= 1.8 && (a.sucre || 0) >= 2.5; },
      effet: function (a, v) { return v.mute ? 20 : (v.botrytis ? -30 : 0); }
    }
  ];

  /* =====================================================================
     BIBLIOTHÈQUE DE PLATS
     ---------------------------------------------------------------------
     Chaque entrée est un ARCHÉTYPE, pas une recette : « poisson grillé »
     couvre le bar, la dorade et le maquereau. Un plat inconnu est
     rattaché à l'archétype le plus proche par ses mots, et à défaut
     traité par les axes déduits de ses ingrédients (voir `ingredients`
     plus bas). On ne force jamais une correspondance douteuse : au-delà
     d'un certain doute, l'assistant le dit et pose une question courte.

     Les mots sont écrits SANS ACCENT et en minuscules : le moteur
     normalise la saisie du client de la même façon.
     ===================================================================== */
  S.plats = [

    /* ---------------------- MER & IODE ---------------------- */
    { id: 'huitres', etape: 'apero', nom: 'Huîtres et coquillages', registre: 2,
      mots: ['huitre', 'huitres', 'coquillage', 'palourde', 'bulot', 'oursin', 'coquille saint jacques crue', 'plateau de fruits de mer', 'fruits de mer'],
      axes: { iode: 3, sel: 2, puissance: 1, acide: 1 },
      familles: { blanc_sec: 96, champagne: 92, effervescent: 82, biere: 40, rose: 25, blanc_riche: 20, rouge: 2 },
      style: { blanc_vif: 1 },
      deconseille: { rouge: 'Le tanin du rouge et l’iode de l’huître donnent un goût métallique : c’est l’un des rares accords franchement à éviter.' },
      raisons: {
        blanc_sec: "Un blanc tendu et salin est le miroir de l'huître : son acidité nettoie la bouche entre chaque coquille et prolonge le goût iodé.",
        champagne: "Les bulles et la vivacité du champagne relèvent la salinité de l'huître sans jamais la couvrir — l'accord de fête par excellence.",
        effervescent: "Des bulles fines et un dosage sec suffisent à répondre à l'iode : l'effervescence rafraîchit la bouche à chaque gorgée.",
        biere: "Une bière blonde sèche, légèrement amère, fait un contrepoint marin surprenant et très désaltérant."
      } },

    { id: 'saumon_fume', etape: 'apero', nom: 'Saumon fumé', registre: 2,
      mots: ['saumon fume', 'saumon', 'truite fumee', 'poisson fume', 'gravlax', 'gravadlax', 'saumon gravlax'],
      axes: { gras: 2, iode: 2, fume: 2, sel: 2, puissance: 1.5 },
      familles: { champagne: 92, blanc_sec: 90, effervescent: 84, blanc_riche: 60, vodka: 45, rose: 30, rouge: 5 },
      style: { blanc_vif: 0.8 },
      raisons: {
        blanc_sec: "La tension du blanc équilibre le gras du saumon fumé, tandis que ses notes d'agrumes prolongent le caractère fumé et iodé de la chair.",
        champagne: "Le champagne joue sur deux tableaux : ses bulles allègent le gras, sa légère note briochée épouse la fumaison.",
        blanc_riche: "Un blanc ample et légèrement boisé répond à la richesse de la chair — l'accord de texture plutôt que de contraste."
      } },

    { id: 'crustaces', etape: 'entree', nom: 'Crustacés', registre: 3,
      mots: ['homard', 'langouste', 'crevette', 'gambas', 'langoustine', 'crabe', 'tourteau', 'ecrevisse', 'txangurro', 'homard thermidor', 'linguine all astice', 'astice', 'ecrevisses', 'thermidor'],
      axes: { iode: 2, gras: 1.5, puissance: 1.5, sel: 1 },
      familles: { blanc_riche: 92, champagne: 88, blanc_sec: 84, effervescent: 74, rose: 40, rouge: 5 },
      raisons: {
        blanc_riche: "La chair sucrée du crustacé demande un blanc ample : le gras du vin répond à celui de la sauce, sans écraser la finesse.",
        champagne: "Un champagne vineux tient tête au homard là où un blanc léger se ferait avaler.",
        blanc_sec: "Sur un crustacé simplement poché, un blanc vif garde l'accord net et marin."
      } },

    { id: 'poisson_grille', etape: 'plat', nom: 'Poisson grillé ou rôti',
      mots: ['poisson grille', 'poisson au four', 'bar', 'dorade', 'daurade', 'maquereau', 'sardine', 'poisson roti', 'lieu', 'cabillaud', 'merlu', 'turbot', 'sole', 'meuniere', 'sauce vierge'],
      axes: { iode: 2, puissance: 1.3, fume: 1 },
      familles: { blanc_sec: 94, blanc_riche: 78, champagne: 66, effervescent: 62, rose: 55, rouge: 12 },
      style: { blanc_vif: 0.7 },
      raisons: {
        blanc_sec: "Un blanc sec et droit respecte la finesse du poisson : il apporte l'acidité qui remplace le filet de citron.",
        blanc_riche: "Sur une chair grasse ou une peau grillée, un blanc plus ample tient la longueur sans dominer.",
        rose: "Un rosé sec fonctionne très bien sur un poisson grillé au barbecue, entre la fraîcheur du blanc et le fruit du rouge."
      } },

    { id: 'poisson_sauce', etape: 'plat', nom: 'Poisson en sauce', registre: 2,
      mots: ['poisson en sauce', 'sauce beurre blanc', 'beurre blanc', 'blanquette de poisson',
             'brandade', 'bouillabaisse', 'matelote', 'quenelle', 'lotte', 'baudroie',
             'saint pierre', 'saint-pierre', 'fletan', 'congre', 'anguille', 'rouget',
             'rascasse', 'grenadier', 'poisson roti au four', 'st pierre', 'nantua', 'normande'],
      axes: { gras: 2.5, iode: 1.5, umami: 1, puissance: 1.8 },
      familles: { blanc_riche: 95, blanc_sec: 76, champagne: 62, effervescent: 55, rose: 35, rouge: 10 },
      raisons: {
        blanc_riche: "Le beurre de la sauce appelle un blanc gras et vineux : deux textures qui se ressemblent s'accordent mieux qu'elles ne s'opposent.",
        blanc_sec: "Si la sauce est déjà généreuse, un blanc plus vif vient au contraire l'alléger."
      } },

    { id: 'sushi', etape: 'plat', nom: 'Poisson cru, sushi, tartare',
      mots: ['sushi', 'sashimi', 'poisson cru', 'tartare de poisson', 'ceviche', 'carpaccio de poisson', 'maki', 'poke bowl', 'chirashi'],
      axes: { iode: 2.5, acide: 1.5, puissance: 1 },
      familles: { blanc_sec: 92, champagne: 86, effervescent: 78, rose: 45, biere: 40, the: 55, rouge: 3 },
      style: { blanc_vif: 1 },
      deconseille: { rouge: 'Sur une chair crue, le tanin durcit et masque tout : le rouge est ici à contre-emploi.' },
      raisons: {
        blanc_sec: "Sur une chair crue, tout se joue sur la pureté : un blanc sec et salin souligne le poisson sans rien ajouter.",
        champagne: "Les bulles réveillent le poisson cru et effacent le sésame ou le soja entre deux bouchées."
      } },

    /* ---------------------- FOIE GRAS & CHARCUTERIE ---------------------- */
    { id: 'foie_gras', etape: 'entree', nom: 'Foie gras', registre: 3,
      mots: ['foie gras', 'foie-gras', 'terrine de foie gras', 'bloc de foie gras'],
      axes: { gras: 3, sucre: 1, puissance: 2.2, sel: 1 },
      /* Le vin doux naturel reste un grand accord du foie gras — la maison
         le pratique : sa recette du foie gras mi-cuit lie le Monbazillac
         ET le Rivesaltes 20 ans. On ne le fait pas reculer. */
      familles: { liquoreux: 98, moelleux: 92, vdn: 82, champagne: 74, blanc_riche: 62, effervescent: 55, armagnac: 40, rouge: 12 },
      raisons: {
        liquoreux: "L'accord périgourdin par excellence : le sucre du liquoreux enrobe le gras du foie, et son acidité empêche l'ensemble d'être lourd.",
        moelleux: "Un moelleux moins puissant qu'un liquoreux laisse davantage parler le foie gras — l'accord juste sans excès de sucre.",
        champagne: "À contre-courant mais redoutable : les bulles tranchent le gras et remettent la bouche à zéro.",
        vdn: "Un vin doux naturel apporte le sucre attendu avec une note d'écorce d'orange qui réveille la terrine."
      } },

    { id: 'charcuterie', etape: 'apero', nom: 'Charcuterie et terrines',
      mots: ['charcuterie', 'saucisson', 'jambon', 'terrine', 'pate', 'rillettes', 'chorizo', 'planche', 'grillons', 'grillons du perigord', 'prosciutto', 'coppa', 'pancetta', 'bresaola'],
      axes: { gras: 2, sel: 2.5, puissance: 1.8, umami: 1.5 },
      familles: { rouge: 84, rose: 74, blanc_sec: 66, effervescent: 62, champagne: 60, pastis: 48, biere: 45 },
      style: { rouge_leger: 1 },
      raisons: {
        rouge: "Un rouge fruité et peu tannique nettoie le gras du saucisson sans se battre avec le sel — le tanin, lui, deviendrait métallique.",
        rose: "Le rosé sec est l'allié discret de la planche : assez de fruit pour la charcuterie, assez de fraîcheur pour l'été.",
        biere: "L'amertume et les bulles de la bière font le ménage entre deux tranches grasses."
      } },

    /* ---------------------- VIANDES ---------------------- */
    { id: 'viande_rouge', etape: 'plat', nom: 'Viande rouge grillée', registre: 2,
      mots: ['entrecote', 'cote de boeuf', 'boeuf', 'steak', 'bavette', 'faux filet', 'onglet', 'grillade', 'barbecue', 'viande rouge', 'rumsteck', 'carne asada', 'picanha', 'asado', 'churrasco', 'chimichurri', 'bistecca', 'bistecca alla fiorentina', 'tagliata', 'fiorentina', 'bearnaise', 'bordelaise'],
      axes: { tanin: 3, gras: 2, fume: 2, puissance: 2.5, umami: 1.5 },
      familles: { rouge: 98, biere: 45, rose: 25, blanc_riche: 12, whisky: 10 },
      style: { rouge_puissant: 1 },
      raisons: {
        rouge: "Le tanin du vin se lie aux protéines de la viande : c'est cette rencontre qui assouplit le vin et rend la viande plus tendre en bouche.",
        biere: "Une bière ambrée ou brune répond au grillé du barbecue par ses notes torréfiées."
      } },

    { id: 'piece_de_boeuf', etape: 'plat', nom: 'Belle pièce de bœuf', registre: 3,
      mots: ['cote de boeuf', 'filet de boeuf', 'tournedos', 'chateaubriand', 'rossini', 'wellington', 'pave de boeuf', 'beef wellington', 'roti de boeuf'],
      axes: { tanin: 3, gras: 2.2, fume: 1.5, puissance: 2.8, umami: 2 },
      familles: { rouge: 98, blanc_riche: 10 },
      style: { rouge_puissant: 1.3 },
      raisons: {
        rouge: "Une belle pièce de bœuf mérite un rouge à sa hauteur : il faut de la structure et des tanins mûrs pour tenir la mâche et le gras persillé de la viande."
      } },

    { id: 'canard', etape: 'plat', nom: 'Canard, magret, confit', registre: 2,
      mots: ['magret', 'canard', 'confit de canard', 'canard aux cepes', 'gesier', 'parmentier de canard', 'confit'],
      axes: { gras: 2.5, tanin: 2.5, umami: 2, puissance: 2.4 },
      familles: { rouge: 96, vdn: 45, moelleux: 30, armagnac: 35, rose: 18, blanc_riche: 15 },
      style: { rouge_puissant: 0.8 },
      terroir: ['bergerac', 'perigord', 'pecharmant', 'monbazillac', 'montravel', 'saussignac',
                'cahors', 'madiran', 'gaillac', 'fronton', 'buzet', 'duras', 'marmandais',
                'jurancon', 'saint mont', 'tursan', 'brulhois', 'sud ouest', 'gascogne'],
      raisons: {
        rouge: "Le gras du magret demande du tanin pour être nettoyé, et son goût franc supporte un rouge structuré : les vins du Sud-Ouest sont chez eux.",
        vdn: "Sur un canard laqué au miel ou aux fruits, un vin doux naturel fait écho au sucré de la sauce.",
        armagnac: "En fin de repas, l'armagnac prolonge le Sud-Ouest — la même terre dans le verre."
      } },

    { id: 'gibier', etape: 'plat', nom: 'Gibier', registre: 3,
      mots: ['gibier', 'chevreuil', 'sanglier', 'marcassin', 'biche', 'lievre', 'faisan', 'perdrix', 'civet', 'venaison', 'poivrade', 'grand veneur', 'veneur'],
      axes: { tanin: 3, umami: 3, puissance: 3, gras: 2 },
      familles: { rouge: 98, vdn: 25, armagnac: 30, biere: 20 },
      style: { rouge_puissant: 1.3 },
      raisons: {
        rouge: "Le gibier a du caractère et du goût de sous-bois : il lui faut un rouge de garde, concentré, dont les tanins ont commencé à se fondre."
      } },

    { id: 'agneau', etape: 'plat', nom: 'Agneau', registre: 2,
      mots: ['agneau', 'gigot', 'carre d agneau', 'souris d agneau', 'mouton', 'souris', 'printanier'],
      axes: { tanin: 2.5, gras: 2, puissance: 2.3, umami: 1.5 },
      familles: { rouge: 96, rose: 25, blanc_riche: 12 },
      style: { rouge_puissant: 0.7 },
      raisons: {
        rouge: "L'agneau et le cabernet se répondent depuis toujours : le tanin fond sur le gras de la viande, et les notes d'herbes du vin rappellent le thym du gigot."
      } },

    { id: 'volaille', etape: 'plat', nom: 'Volaille rôtie',
      mots: ['poulet', 'volaille', 'chapon', 'dinde', 'pintade', 'poularde', 'coq', 'poule au pot', 'lapin', 'basquaise', 'vallee d auge'],
      axes: { gras: 1.8, umami: 1.5, puissance: 1.8, tanin: 1.2 },
      familles: { blanc_riche: 82, rouge: 84, champagne: 60, blanc_sec: 62, effervescent: 50, rose: 45 },
      style: { rouge_leger: 0.8 },
      raisons: {
        blanc_riche: "Une volaille rôtie a la chair douce et la peau grasse : un blanc ample l'accompagne mieux qu'un rouge qui la couvrirait.",
        rouge: "Un rouge souple et fruité, servi frais, respecte la finesse de la chair tout en tenant tête à la peau dorée."
      } },

    { id: 'porc', etape: 'plat', nom: 'Porc et cochonnailles',
      mots: ['porc', 'cochon', 'travers', 'echine', 'roti de porc', 'saucisse', 'andouillette', 'boudin', 'choucroute', 'enchaud', 'enchaud perigourdin', 'echine de porc', 'rouelle de porc', 'jambonneau', 'palette', 'travers'],
      axes: { gras: 2.3, sel: 1.8, puissance: 1.9, umami: 1.5 },
      familles: { rouge: 80, blanc_sec: 76, biere: 70, rose: 55, blanc_riche: 60, effervescent: 40 },
      style: { rouge_leger: 0.8 },
      raisons: {
        blanc_sec: "Un blanc vif est le contrepoint classique du cochon : l'acidité coupe le gras là où un rouge tannique l'alourdirait.",
        rouge: "Un rouge léger et fruité, sans excès de tanin, accompagne la saucisse grillée sans la dominer.",
        biere: "Sur une choucroute ou un travers laqué, la bière est l'accord de tradition."
      } },

    { id: 'plat_mijote', etape: 'plat', nom: 'Plat mijoté en sauce', registre: 2,
      mots: ['boeuf bourguignon', 'daube', 'pot au feu', 'blanquette', 'navarin', 'braise', 'mijote', 'ragout', 'cassoulet', 'coq au vin', 'osso bucco', 'tajine', 'couscous', 'chili con carne', 'paella', 'gardiane', 'gardiane de taureau', 'carbonade', 'carbonade flamande', 'potjevleesch', 'mafe', 'maqluba', 'ropa vieja', 'waterzooi', 'baeckeoffe', 'axoa', 'estouffade', 'boeuf strogonoff', 'ossobuco', 'pollo alla cacciatora', 'cacciatora', 'sarma', 'sauerbraten', 'rouladen', 'stoofvlees', 'cou farci', 'boeuf braise', 'bourguignon', 'bourguignonne', 'strogonoff', 'stroganov', 'chasseur'],
      axes: { umami: 3, gras: 2.2, tanin: 2, puissance: 2.4 },
      familles: { rouge: 94, biere: 35, blanc_riche: 30, rose: 15 },
      style: { rouge_puissant: 0.7 },
      raisons: {
        rouge: "Un plat longuement mijoté a fondu ses saveurs : il lui faut un rouge de même caractère, généreux et déjà assoupli, souvent celui qui a servi à la cuisson."
      } },

    /* ---------------------- LÉGUMES, PÂTES, VÉGÉTAL ---------------------- */
    { id: 'legumes', etape: 'plat', nom: 'Légumes et plats végétariens',
      mots: ['legume', 'legumes', 'ratatouille', 'vegetarien', 'vegetalien', 'vegan', 'gratin de legumes', 'risotto de legumes', 'curry de legumes', 'buddha bowl'],
      axes: { acide: 1.5, umami: 1.5, puissance: 1.4 },
      familles: { blanc_sec: 84, rose: 76, rouge: 62, blanc_riche: 60, effervescent: 50 },
      style: { blanc_vif: 0.6, rouge_leger: 1 },
      raisons: {
        blanc_sec: "Les légumes ont une amertume et une acidité propres : un blanc sec les accompagne sans les contrarier.",
        rose: "Un rosé sec fait le lien entre des légumes variés — c'est le vin des assiettes composées."
      } },

    { id: 'champignons', etape: 'plat', nom: 'Champignons et truffe', registre: 2,
      mots: ['champignon', 'cepe', 'cepes', 'girolle', 'morille', 'truffe', 'forestiere', 'polenta e funghi', 'funghi', 'poelee de champignons'],
      axes: { umami: 3, gras: 1.5, puissance: 2 },
      familles: { rouge: 90, blanc_riche: 84, vdn: 25, champagne: 45 },
      style: { rouge_puissant: 0.4 },
      raisons: {
        rouge: "Le sous-bois du champignon et celui d'un rouge un peu évolué parlent la même langue : c'est l'accord d'automne le plus évident.",
        blanc_riche: "Un blanc ample et boisé, sur une poêlée crémée, tient parfaitement l'umami du champignon."
      } },

    { id: 'pates', etape: 'plat', nom: 'Pâtes, pizza, lasagnes',
      mots: ['pate', 'pates', 'lasagne', 'pizza', 'spaghetti', 'tagliatelle', 'risotto', 'gnocchi', 'carbonara', 'bolognaise', 'burger', 'quiche', 'croque monsieur', 'cacio e pepe', 'amatriciana', 'puttanesca', 'pesto', 'pesto alla genovese', 'calzone', 'aglio olio', 'linguine', 'rigatoni', 'penne'],
      axes: { umami: 2, acide: 1.5, gras: 2, puissance: 1.8 },
      familles: { rouge: 88, blanc_sec: 60, rose: 58, biere: 45, blanc_riche: 50 },
      style: { rouge_leger: 0.9 },
      raisons: {
        rouge: "La tomate cuite est acide et sucrée à la fois : un rouge fruité, peu tannique et vif lui répond sans durcir.",
        blanc_riche: "Sur des pâtes à la crème ou aux champignons, un blanc ample fait un meilleur accord qu'un rouge."
      } },

    { id: 'epice', etape: 'plat', nom: 'Plat épicé',
      mots: ['epice', 'epicee', 'piment', 'curry', 'thai', 'indien', 'mexicain', 'colombo', 'harissa', 'pimente', 'rogan josh', 'vindaloo', 'tteokbokki', 'hot pot sichuanais', 'sundubu jjigae'],
      axes: { epice: 3, puissance: 2.2, sucre: 1 },
      familles: { moelleux: 88, rose: 80, blanc_sec: 74, biere: 78, effervescent: 66, tonic: 60, jus: 50, tequila: 52, rouge: 30 },
      style: { blanc_vif: 0.5, rouge_leger: 1.2 },
      deconseille: { rouge: 'L’alcool et le tanin attisent le piment au lieu de l’apaiser : un rouge puissant rendra le plat plus brûlant.' },
      raisons: {
        moelleux: "Le sucre éteint le piment là où l'alcool et le tanin l'attisent : un vin légèrement doux est le vrai allié des plats relevés.",
        blanc_sec: "Un blanc aromatique et frais apaise la brûlure et relance les épices à chaque gorgée.",
        biere: "La bière est la boisson de la cuisine épicée depuis toujours : l'amertume et le froid calment le piment."
      } },

    /* ---------------------- FROMAGES ---------------------- */
    { id: 'fromage_plateau', etape: 'fromage', nom: 'Plateau de fromages', registre: 2,
      mots: ['fromage', 'fromages', 'plateau de fromages', 'plateau de fromage', 'assiette de fromages'],
      axes: { gras: 2.5, sel: 2, umami: 2.5, puissance: 2.2 },
      familles: { blanc_riche: 86, rouge: 78, blanc_sec: 70, liquoreux: 62, champagne: 58, moelleux: 55, vdn: 50 },
      raisons: {
        blanc_riche: "Un plateau mêle des pâtes molles, des pâtes dures et souvent un bleu : un blanc à la fois ample et vif est le seul vin qui les traverse tous sans en heurter aucun.",
        rouge: "Sur un plateau, le rouge doit être assoupli et peu tannique — sinon il durcit sur les pâtes salées et se casse sur le bleu.",
        liquoreux: "S'il y a un bleu sur le plateau, un verre de liquoreux à côté du rouge règle la question : le sel et le sucre se neutralisent."
      } },

    { id: 'fromage_pate_dure', etape: 'fromage', nom: 'Fromages à pâte pressée',
      mots: ['comte', 'beaufort', 'gruyere', 'cantal', 'tomme', 'abondance', 'pate pressee', 'saint nectaire', 'saint-nectaire', 'salers', 'laguiole', 'morbier', 'emmental', 'ossau', 'parmesan', 'pecorino', 'grana padano', 'mimolette', 'st nectaire'],
      axes: { gras: 2, sel: 2, umami: 2.5, puissance: 2 },
      familles: { blanc_riche: 88, rouge: 80, vdn: 55, champagne: 62, blanc_sec: 70 },
      raisons: {
        blanc_riche: "On croit le fromage fait pour le rouge : sur une pâte pressée, un blanc riche est presque toujours meilleur — pas de tanin pour heurter le sel.",
        rouge: "Un rouge assoupli, sans tanin agressif, accompagne un comté jeune sans le combattre."
      } },

    { id: 'fromage_bleu', etape: 'fromage', nom: 'Bleus et persillés', registre: 2,
      mots: ['roquefort', 'fourme', 'gorgonzola', 'stilton', 'persille', 'bleu d auvergne',
             'bleu des causses', 'bleu de gex', 'bleu de bresse', 'fromage bleu', 'bleu de brebis'],
      axes: { sel: 3, gras: 2.5, puissance: 3, umami: 2 },
      familles: { liquoreux: 98, vdn: 90, moelleux: 82, whisky: 52, armagnac: 42, rouge: 30 },
      deconseille: { rouge: 'Le sel du bleu et le tanin du rouge s’exaspèrent l’un l’autre. Si un rouge accompagne le plateau, prévoyez un demi de liquoreux pour le roquefort.' },
      raisons: {
        liquoreux: "Le sel du bleu et le sucre du liquoreux se neutralisent : chacun rend l'autre plus doux — c'est l'accord roquefort-monbazillac.",
        vdn: "Un vin doux naturel apporte le même contrepoint sucré avec une note de fruits confits qui tient tête au persillé."
      } },

    { id: 'fromage_chevre', etape: 'fromage', nom: 'Chèvres et brebis frais',
      mots: ['chevre', 'crottin', 'sainte maure', 'brebis', 'feta', 'ricotta', 'fromage frais', 'cervelle de canut', 'faisselle', 'brousse'],
      axes: { acide: 2, sel: 1.5, puissance: 1.5 },
      familles: { blanc_sec: 94, effervescent: 60, rose: 55, champagne: 55, blanc_riche: 45, rouge: 15 },
      style: { blanc_vif: 1 },
      raisons: {
        blanc_sec: "Le chèvre est acide et crayeux : un sauvignon ou un blanc minéral lui répond exactement — l'accord Sancerre-crottin est un classique fondé."
      } },

    { id: 'fromage_croute', etape: 'fromage', nom: 'Croûtes fleuries et lavées',
      mots: ['camembert', 'brie', 'coulommiers', 'munster', 'epoisses', 'reblochon', 'mont d or', 'maroilles', 'livarot'],
      axes: { gras: 3, umami: 2.5, sel: 2, puissance: 2.5 },
      familles: { champagne: 78, blanc_riche: 84, biere: 60, moelleux: 55, rouge: 45, effervescent: 60 },
      raisons: {
        blanc_riche: "Ces pâtes crémeuses saturent la bouche : un blanc ample et vif à la fois relance l'appétit là où un rouge tannique bloquerait.",
        champagne: "Les bulles nettoient le gras d'un brie coulant — un accord de fête souvent oublié."
      } },

    /* ---------------------- DESSERTS ---------------------- */
    { id: 'chocolat', etape: 'dessert', nom: 'Desserts au chocolat',
      mots: ['chocolat', 'sachertorte', 'sacher', 'devil s food', 'fondant au chocolat', 'fondant chocolat', 'moelleux au chocolat', 'brownie', 'mousse au chocolat', 'truffe au chocolat', 'ganache', 'foret noire', 'opera', 'coulant au chocolat', 'torta caprese'],
      axes: { sucre: 3, amer: 2, gras: 2.5, puissance: 2.8 },
      familles: { vdn: 94, liquoreux: 74, moelleux: 58, rhum: 46, armagnac: 44, cognac: 42, whisky: 38 },
      deconseille: { rouge: 'Un rouge sec paraît maigre et amer derrière le chocolat : il faut un vin plus sucré que le dessert.', blanc_sec: 'Un blanc sec est effacé par le cacao — le sucré doit venir du verre autant que de l’assiette.' },
      raisons: {
        vdn: "Le chocolat noir est amer et gras : il faut un vin plus sucré que lui, sinon le vin paraît sec. Un banyuls ou un maury est taillé pour cela.",
        rhum: "Le rhum vieux et le cacao partagent des notes de fruits confits et de bois torréfié — l'accord se fait tout seul.",
        armagnac: "L'armagnac apporte le fruit sec et le rancio qui prolongent le chocolat sans le sucrer davantage."
      } },

    { id: 'fruits', etape: 'dessert', nom: 'Desserts aux fruits',
      /* ⚠️ Jamais le mot « fruits » seul : il attrapait « fruits de mer », et
         un plateau d'huîtres héritait de la mise en garde d'un dessert
         contre le blanc sec. On nomme les desserts, pas une catégorie. */
      /* ⚠️ Ni « pomme » ni « poire » tout court : « pommes dauphine »,
         « pommes Anna » et « pommes Pont-Neuf » sont des garnitures de
         pommes de TERRE, et elles arrivaient en dessert aux fruits. On
         nomme la préparation, jamais le fruit seul. */
      mots: ['tarte aux', 'tarte au citron', 'tarte tatin', 'fruits rouges', 'fruits de saison',
             'fraise', 'framboise', 'peche', 'abricot', 'poires pochees', 'poire belle helene',
             'tarte aux pommes', 'compote de pommes', 'pommes au four', 'salade de fruits',
             'sorbet', 'crumble', 'clafoutis', 'tarte aux fruits', 'ananas roti', 'figues roties', 'poires belle helene', 'soupe de fraises', 'carpaccio d ananas', 'salade d agrumes', 'salade d agrume', 'tatin'],
      supplante: ['salade'],
      axes: { sucre: 2.5, acide: 1.5, puissance: 1.8 },
      familles: { moelleux: 88, liquoreux: 82, champagne: 74, effervescent: 78, vdn: 55, calvados: 40 },
      deconseille: { rouge: 'Un vin sec derrière un dessert sucré paraît toujours plus acide et plus dur qu’il n’est.', blanc_sec: 'Le sucre du dessert fait paraître le blanc sec agressif : il lui faut au moins un demi-sec.' },
      raisons: {
        moelleux: "Sur un dessert aux fruits, le vin doit être au moins aussi sucré que l'assiette : un moelleux garde de l'acidité et reste digeste.",
        effervescent: "Un effervescent demi-sec allège un dessert aux fruits — les bulles font office de fraîcheur.",
        calvados: "Sur une tarte aux pommes, le calvados est l'évidence normande."
      } },

    { id: 'creme', etape: 'dessert', nom: 'Desserts crémeux et pâtissiers',
      mots: ['creme brulee', 'tiramisu', 'ile flottante', 'panna cotta', 'cheesecake', 'mille feuille', 'paris brest', 'eclair', 'profiterole', 'macaron', 'flan', 'riz au lait', 'teurgoule', 'creme catalane', 'brioche perdue', 'pain perdu', 'creme patissiere', 'creme anglaise', 'creme chantilly', 'creme dessert', 'creme au chocolat', 'panna'],
      axes: { sucre: 3, gras: 2.5, puissance: 2 },
      familles: { liquoreux: 90, moelleux: 86, vdn: 68, champagne: 55, rhum: 50, cognac: 45 },
      deconseille: { rouge: 'Sur une crème sucrée, un rouge sec devient anguleux : le verre doit être au moins aussi doux que l’assiette.' },
      raisons: {
        liquoreux: "La rondeur d'un liquoreux répond à celle de la crème, et son acidité empêche l'ensemble d'être écœurant.",
        rhum: "Le rhum vieux et la vanille d'un dessert crémeux partagent le même registre."
      } },

    /* ---------------------- APÉRITIF & DIVERS ---------------------- */
    { id: 'apero_sale', etape: 'apero', nom: 'Amuse-bouches salés',
      mots: ['gougere', 'olive', 'cacahuete', 'chips', 'tapenade', 'toast', 'verrine', 'feuillete', 'amuse bouche', 'aperitif dinatoire', 'tapas', 'socca', 'msemen', 'gougeres'],
      axes: { sel: 2.5, gras: 2, puissance: 1.3 },
      familles: { champagne: 88, effervescent: 84, blanc_sec: 80, rose: 72, biere: 60, gin: 64, pastis: 62, sans_alcool_spiritueux: 58, tonic: 56, whisky: 46, vodka: 46, rouge: 40 },
      raisons: {
        champagne: "Un apéritif est fait de bouchées salées et grasses : les bulles remettent le palais à zéro entre chacune.",
        effervescent: "Un crémant sec offre exactement la même mécanique qu'un champagne, à un tarif d'apéritif."
      } },

    { id: 'salade', etape: 'entree', nom: 'Salades et crudités',
      mots: ['salade', 'crudite', 'carpaccio', 'gaspacho', 'ceviche de legumes', 'taboule', 'vinaigrette', 'fattoush', 'panzanella verte', 'salade de lentilles'],
      axes: { acide: 2.5, puissance: 1 },
      familles: { blanc_sec: 86, rose: 78, effervescent: 60, champagne: 55, rouge: 15 },
      style: { blanc_vif: 1 },
      deconseille: { rouge: 'La vinaigrette écrase le rouge et fait ressortir ses tanins : mieux vaut un blanc vif.' },
      raisons: {
        blanc_sec: "La vinaigrette est le pire ennemi du vin : seul un blanc franchement vif tient face à son acidité sans paraître mou."
      } },

    /* SALADES COMPOSÉES DU SUD-OUEST — le mot « salade » ne dit pas tout.
       Une périgourdine, c'est gésiers confits, magret séché, foie gras et
       noix sur quelques feuilles : le gras et la viande y pèsent bien plus
       que la vinaigrette. Sans cette entrée, seul « salade » était reconnu
       et l'assistant servait un blanc vif sur du confit de canard — une
       faute d'accord que n'importe quel client d'ici voit tout de suite.
       `leve_deconseille` annule la mise en garde de « salade » contre le
       rouge : c'est bien la même assiette, mais elle a changé de nature. */
    { id: 'salade_sud_ouest', etape: 'entree', nom: 'Salade composée du Sud-Ouest', registre: 2,
      mots: ['perigourdine', 'landaise', 'gasconne', 'quercynoise', 'salade du sud ouest',
             'salade au gesier', 'salade aux gesiers', 'salade de gesiers', 'salade au magret',
             'salade au foie gras', 'salade au confit'],
      axes: { gras: 2.2, tanin: 1.8, umami: 1.8, acide: 1, puissance: 2 },
      familles: { rouge: 92, rose: 58, moelleux: 45, blanc_riche: 34, blanc_sec: 18, effervescent: 15 },
      style: { rouge_leger: 0.6 },
      leve_deconseille: ['rouge'],
      supplante: ['salade'],
      /* PAS DE BLANC ICI, JAMAIS. Le gras du confit et du magret domine
         l'assiette : le caviste verse un rouge souple ou un rosé de
         caractère, et rien d'autre. Ce n'est pas une préférence de
         score qu'un bon vin blanc pourrait renverser un jour de stock
         inhabituel, c'est une porte fermée. Le client garde la main :
         s'il demande explicitement du blanc, on le lui sert. */
      couleurs_admises: ['rouge', 'rose'],
      terroir: ['bergerac', 'perigord', 'pecharmant', 'monbazillac', 'montravel', 'saussignac',
                'cahors', 'madiran', 'gaillac', 'fronton', 'buzet', 'duras', 'marmandais',
                'jurancon', 'saint mont', 'tursan', 'brulhois', 'sud ouest', 'gascogne'],
      raisons: {
        rouge: "Gésiers confits, magret et noix : c'est du canard avant d'être une salade. Un rouge du Sud-Ouest, souple plutôt que tannique, nettoie le gras sans écraser les feuilles.",
        rose: "Un rosé de caractère passe la vinaigrette et tient le confit — l'accord d'été de la périgourdine.",
        moelleux: "S'il y a du foie gras dans l'assiette, un moelleux fait le pont entre le gras et le sucre, comme à l'entrée."
      } },

    /* Les autres salades composées grasses : même mécanique, sans le
       Sud-Ouest. Le chèvre chaud et la césar ne réclament pas de rouge,
       mais ils ne méritent pas non plus la mise en garde de la
       vinaigrette : le fromage et la crème l'ont déjà tamponnée. */
    { id: 'salade_composee', etape: 'entree', nom: 'Salade composée gourmande',
      mots: ['salade cesar', 'cesar', 'chevre chaud', 'salade au chevre', 'salade nicoise',
             'niçoise', 'nicoise', 'salade au poulet', 'salade au lardon', 'salade aux lardons'],
      axes: { gras: 1.6, acide: 1.5, sel: 1, puissance: 1.4 },
      familles: { blanc_sec: 80, rose: 74, effervescent: 52, rouge: 40, blanc_riche: 45 },
      style: { blanc_vif: 0.6 },
      leve_deconseille: ['rouge'],
      supplante: ['salade'],
      raisons: {
        blanc_sec: "Le fromage et la vinaigrette tirent chacun de leur côté : un blanc vif mais un peu rond tient les deux.",
        rose: "Un rosé sec fait le lien entre la fraîcheur des feuilles et le gras du fromage ou des lardons."
      } },

    { id: 'soupe', etape: 'entree', nom: 'Soupes et veloutés',
      mots: ['soupe', 'veloute', 'potage', 'bouillon', 'consomme', 'harira', 'soupe won ton', 'won ton', 'tom kha', 'tom kha gai', 'soupe au pistou', 'caldo verde', 'erwtensoep', 'soupe de pois casses', 'pistou'],
      axes: { gras: 1.5, umami: 2, puissance: 1.3 },
      familles: { blanc_sec: 78, blanc_riche: 76, champagne: 55, effervescent: 50, rouge: 25 },
      raisons: {
        blanc_riche: "Un velouté est doux et lié : un blanc rond l'accompagne en douceur, sans rupture de texture."
      } },

    { id: 'raclette', etape: 'plat', nom: 'Raclette, fondue, tartiflette',
      mots: ['raclette', 'fondue', 'tartiflette', 'mont d or chaud', 'croziflette', 'savoyard'],
      axes: { gras: 3, sel: 2.5, umami: 2.5, puissance: 2.2 },
      familles: { blanc_sec: 92, blanc_riche: 70, rouge: 55, biere: 45, effervescent: 40 },
      style: { blanc_vif: 1 },
      raisons: {
        blanc_sec: "Le fromage fondu tapisse la bouche : il faut un blanc très vif — un vin de Savoie — pour la relancer à chaque bouchée. Un rouge tannique, lui, tourne au métallique.",
        rouge: "Si un rouge s'impose, qu'il soit léger, fruité et servi frais : jamais un vin de garde."
      } },

    { id: 'cafe', etape: 'digestif', nom: 'Café et fin de repas',
      mots: ['cafe', 'digestif', 'fin de repas', 'mignardise', 'chocolat noir'],
      axes: { amer: 2, puissance: 2.5 },
      /* La liqueur recule nettement : sa fiche mentionne « café », elle
         raflait donc l'accord — une liqueur de café servie avec le café
         est une redondance. On sert une eau-de-vie. */
      familles: { rhum: 92, armagnac: 90, cognac: 88, whisky: 86, calvados: 76, vdn: 74, eau_de_vie: 70, punch: 66, tequila: 64, the: 60, liqueur: 42, liquoreux: 40 },
      raisons: {
        armagnac: "L'armagnac se sert après le café, à température de la pièce : sa chaleur et son rancio referment le repas.",
        rhum: "Un rhum vieux prolonge le café par ses notes de fruits confits et de bois."
      } },

    /* ===================================================================
       COMPLÉMENT DU 2026-08-08
       Un audit de 149 plats courants en a trouvé 28 que la bibliothèque
       ne reconnaissait pas : leur accord retombait alors sur le profil
       MOYEN de l'étape, c'est-à-dire sur rien de précis — d'où des rouges
       proposés sur des Saint-Jacques. Neuf autres n'étaient reconnus que
       par ressemblance, avec des rapprochements franchement mauvais :
       « côte de veau » lue comme du comté, « galette des rois » comme une
       raclette, « pâté en croûte » comme un plat de pâtes. Les entrées
       qui suivent nomment ces plats à la lettre : un mot exact chasse
       toujours une ressemblance.
       =================================================================== */

    { id: 'saint_jacques', etape: 'entree', nom: 'Saint-Jacques et coquillages fins', registre: 2,
      mots: ['saint jacques', 'saint-jacques', 'coquille saint jacques', 'noix de saint jacques', 'petoncle', 'ormeau', 'st jacques', 'coquille st jacques', 'coquilles st jacques', 'noix de st jacques'],
      axes: { iode: 2, gras: 1.5, sucre: 0.5, puissance: 1.5 },
      familles: { blanc_riche: 94, blanc_sec: 86, champagne: 82, effervescent: 68, rose: 25, rouge: 4 },
      style: { blanc_riche: 0.8 },
      deconseille: { rouge: 'La chair délicate et sucrée de la Saint-Jacques est écrasée par le tanin : elle appelle un blanc.' },
      raisons: {
        blanc_riche: "La noix de Saint-Jacques est sucrée et beurrée : un blanc ample, légèrement boisé, épouse cette texture sans la couvrir.",
        champagne: "Sur des Saint-Jacques justes snackées, un champagne vineux fait un accord de fête que rien ne dépasse.",
        blanc_sec: "En carpaccio ou au citron, la Saint-Jacques demande au contraire un blanc tendu et minéral."
      } },

    { id: 'moules', etape: 'plat', nom: 'Moules et coquillages',
      mots: ['moule', 'moules marinieres', 'mouclade', 'palourde', 'coque', 'bulot', 'praire', 'vongole', 'alle vongole', 'clam', 'clams', 'coques', 'tellines'],
      axes: { iode: 2.5, sel: 1.5, puissance: 1 },
      familles: { blanc_sec: 92, effervescent: 60, biere: 58, champagne: 50, rose: 35, rouge: 4 },
      style: { blanc_vif: 1 },
      deconseille: { rouge: "L'iode du coquillage et le tanin du rouge donnent un goût métallique : c'est l'accord à éviter." },
      raisons: {
        blanc_sec: "Un blanc sec et salin est le vin de la marinière : il reprend le bouillon là où il s'arrête.",
        biere: "Moules-frites appelle la bière blonde : l'amertume nettoie la friture, l'usage a raison."
      } },

    { id: 'oeufs', etape: 'entree', nom: 'Œufs en entrée',
      mots: ['oeuf', 'oeufs mimosa', 'oeuf mayonnaise', 'oeuf poche', 'oeufs meurette', 'omelette', 'chakchouka', 'shakshuka', 'piperade'],
      axes: { gras: 1.5, puissance: 1 },
      familles: { blanc_sec: 78, effervescent: 58, blanc_riche: 55, rouge: 30, champagne: 45 },
      raisons: {
        blanc_sec: "Le jaune d'œuf nappe le palais : il faut un blanc vif pour le relever, jamais un vin lourd.",
        rouge: "Les œufs en meurette font exception : cuits au vin rouge, ils se boivent avec le même vin."
      } },

    { id: 'tartinables', etape: 'apero', nom: 'Tartinables et mezze',
      mots: ['tarama', 'houmous', 'tapenade', 'caviar d aubergine', 'guacamole', 'tzatziki', 'mezze', 'falafel', 'labneh', 'moutabal', 'baba ghanoush', 'poutargue et beurre', 'baccala mantecato', 'brandade en tartine'],
      axes: { gras: 1.5, sel: 1.5, acide: 1, puissance: 1 },
      familles: { rose: 86, blanc_sec: 84, effervescent: 66, pastis: 72, gin: 52, tonic: 52, sans_alcool_spiritueux: 52, rouge: 35 },
      style: { blanc_vif: 0.8 },
      raisons: {
        blanc_sec: "Ces préparations sont grasses et salées : un blanc vif remet le palais à zéro entre deux bouchées.",
        rose: "Un rosé sec accompagne le mezze sans jamais prendre le pas sur les épices douces."
      } },

    { id: 'asperge', etape: 'entree', nom: 'Asperges et artichaut',
      mots: ['asperge', 'artichaut', 'pointes d asperges'],
      axes: { amer: 1.5, acide: 1, puissance: 0.8 },
      familles: { blanc_sec: 88, effervescent: 52, rose: 40, blanc_riche: 38, rouge: 3 },
      style: { blanc_vif: 1 },
      deconseille: { rouge: "L'asperge et l'artichaut contiennent une molécule qui rend le vin rouge métallique et amer : c'est le pire accord de la table." },
      raisons: {
        blanc_sec: "L'asperge est l'ennemie déclarée du vin. Seul un blanc très vif, style sauvignon, en sort indemne — et encore, servi frais."
      } },

    { id: 'escargot', etape: 'entree', nom: 'Escargots et grenouilles',
      mots: ['escargot', 'grenouille', 'cuisses de grenouilles', 'escargot de bourgogne'],
      axes: { gras: 2, sel: 1, puissance: 1.3 },
      familles: { blanc_sec: 90, blanc_riche: 72, effervescent: 45, rouge: 25, champagne: 40 },
      style: { blanc_vif: 0.8 },
      raisons: {
        blanc_sec: "Le beurre d'ail demande un blanc franc et acide : c'est lui qui rince le gras entre chaque coquille."
      } },

    { id: 'veau', etape: 'plat', nom: 'Veau', registre: 2,
      mots: ['veau', 'cote de veau', 'blanquette de veau', 'veau marengo', 'escalope milanaise',
             'osso buco', 'paupiette', 'ris de veau', 'escalope de veau', 'scaloppine', 'piccata', 'piccata di vitello', 'tafelspitz'],
      axes: { gras: 1.5, umami: 1.5, puissance: 1.6 },
      familles: { rouge: 72, blanc_riche: 74, blanc_sec: 55, rose: 40, effervescent: 25 },
      style: { rouge_leger: 0.8 },
      raisons: {
        blanc_riche: "La chair du veau est claire et douce : un blanc ample lui va souvent mieux qu'un rouge, surtout en sauce crémée.",
        rouge: "Sur une côte de veau grillée, un rouge souple et peu tannique suffit — jamais une bouteille de garde."
      } },

    { id: 'gratin_viande', etape: 'plat', nom: 'Gratins et plats de ménage',
      mots: ['hachis parmentier', 'parmentier', 'gratin', 'lasagne', 'moussaka', 'shepherd s pie', 'petits farcis', 'farcis nicois', 'legumes farcis', 'janssons frestelse', 'gratin de cardons', 'salsifis a la creme', 'labskaus', 'kottbullar', 'boulettes suedoises'],
      axes: { gras: 2, umami: 2, puissance: 1.8 },
      familles: { rouge: 88, rose: 40, blanc_riche: 30, biere: 25 },
      style: { rouge_leger: 0.7 },
      raisons: {
        rouge: "Viande hachée, tomate et gratiné : un rouge fruité et souple, servi frais, accompagne sans alourdir."
      } },

    { id: 'gibier_plume', etape: 'plat', nom: 'Gibier à plume', registre: 3,
      mots: ['perdreau', 'perdrix', 'faisan', 'caille', 'pigeon', 'becasse', 'palombe'],
      axes: { umami: 2.5, tanin: 2, gras: 1.5, puissance: 2.5 },
      familles: { rouge: 94, vdn: 30, blanc_riche: 25 },
      style: { rouge_puissant: 0.7 },
      raisons: {
        rouge: "Le gibier à plume est plus fin que le poil : un rouge de garde, mais élégant, sans excès de tanin."
      } },

    { id: 'asiatique_frit', etape: 'apero', nom: 'Nems, beignets, fritures',
      mots: ['nem', 'nems', 'samoussa', 'beignet', 'tempura', 'gyoza', 'raviolis vapeur'],
      axes: { gras: 2, sel: 1.5, puissance: 1.2 },
      familles: { effervescent: 86, blanc_sec: 82, champagne: 76, biere: 70, moelleux: 60, rose: 58, gin: 48, tonic: 46, rouge: 10 },
      raisons: {
        effervescent: "La friture appelle des bulles : elles décrochent le gras du palais mieux que n'importe quel vin tranquille."
      } },

    { id: 'bouillon_asiatique', etape: 'plat', nom: 'Ramen, phở et bouillons',
      mots: ['ramen', 'pho', 'bo bun', 'soupe asiatique', 'bouillon asiatique', 'udon'],
      axes: { umami: 2.5, sel: 2, puissance: 1.4 },
      familles: { blanc_sec: 74, biere: 72, rose: 45, effervescent: 45, rouge: 30 },
      raisons: {
        biere: "Un bouillon salé et umami se boit avec une bière fraîche : le vin y perd son fruit.",
        blanc_sec: "À défaut, un blanc sec et aromatique tient face au bouillon sans lutter contre lui."
      } },

    { id: 'mexicain', etape: 'plat', nom: 'Tacos, fajitas et cuisine mexicaine',
      mots: ['tacos', 'fajitas', 'burrito', 'quesadilla', 'chili con carne', 'enchilada', 'nachos', 'mole', 'mole poblano', 'cochinita pibil', 'carnitas'],
      axes: { epice: 2, gras: 1.8, umami: 1.5, puissance: 1.8 },
      familles: { biere: 86, rose: 78, rouge: 70, tequila: 78, blanc_sec: 60, moelleux: 55, tonic: 50 },
      style: { rouge_leger: 0.8 },
      raisons: {
        rouge: "Le piment avive l'alcool et le tanin : on choisit un rouge fruité, souple et servi frais, jamais un vin puissant.",
        biere: "Une bière fraîche éteint le piment là où le vin l'attise."
      } },

    { id: 'grec', etape: 'plat', nom: 'Cuisine grecque et méditerranéenne',
      mots: ['souvlaki', 'gyros', 'keftedes', 'feta', 'dolma', 'pita'],
      axes: { gras: 1.5, sel: 1.5, acide: 1.2, epice: 0.8, puissance: 1.5 },
      familles: { rouge: 70, rose: 74, blanc_sec: 68, effervescent: 30 },
      style: { rouge_leger: 0.7 },
      raisons: {
        rose: "Herbes, citron et grillade : un rosé sec de caractère est l'accord le plus juste de la table méditerranéenne.",
        rouge: "Sur la viande grillée et la feta, un rouge léger servi frais passe très bien."
      } },

    { id: 'tarte_salee', etape: 'entree', nom: 'Tartes salées et flammekueche',
      mots: ['tarte a l oignon', 'tarte salee', 'flammekueche', 'pissaladiere', 'quiche', 'tarte aux poireaux', 'manakish', 'socca au four', 'kalakukko', 'karjalanpiirakka', 'tourte au poisson'],
      axes: { gras: 2, umami: 1.5, puissance: 1.2 },
      familles: { blanc_sec: 84, effervescent: 62, blanc_riche: 60, rose: 45, rouge: 30 },
      raisons: {
        blanc_sec: "Pâte beurrée et garniture fondante : un blanc sec et frais empêche l'ensemble de peser."
      } },

    { id: 'aligot', etape: 'plat', nom: 'Aligot, truffade et plats au fromage fondu',
      mots: ['aligot', 'truffade', 'gratin dauphinois', 'polenta cremeuse'],
      axes: { gras: 2.5, sel: 1.5, puissance: 1.6 },
      familles: { rouge: 70, blanc_sec: 74, rose: 40 },
      style: { rouge_leger: 0.9, blanc_vif: 0.6 },
      raisons: {
        blanc_sec: "Le fromage fondu tapisse la bouche : il faut de l'acidité, pas de la puissance.",
        rouge: "Un rouge léger et frais du Massif central est l'accord du pays — souple, jamais tannique."
      } },

    { id: 'pate_en_croute', etape: 'entree', nom: 'Pâté en croûte et terrines en croûte',
      mots: ['pate en croute', 'pate croute', 'terrine en croute', 'friand', 'saucisson brioche', 'saucisson en brioche'],
      supplante: ['kouglof'],
      axes: { gras: 2.2, sel: 1.8, umami: 1.5, puissance: 1.6 },
      familles: { rouge: 74, blanc_sec: 70, effervescent: 55, moelleux: 45, champagne: 50 },
      style: { rouge_leger: 0.8 },
      raisons: {
        rouge: "Le pâté en croûte est gras et salé : un rouge léger et frais, style beaujolais ou pinot, le relève sans le charger.",
        blanc_sec: "Un blanc sec et vif est l'autre grand classique, notamment sur un pâté en croûte à la volaille."
      } },

    { id: 'galette_sarrasin', etape: 'plat', nom: 'Galette de sarrasin et crêpes salées',
      mots: ['galette bretonne', 'galette de sarrasin', 'galette complete', 'crepe salee', 'crepe au jambon'],
      axes: { gras: 1.8, sel: 1.5, puissance: 1.1 },
      familles: { biere: 70, blanc_sec: 72, effervescent: 55, rose: 45, rouge: 25 },
      raisons: {
        blanc_sec: "Le sarrasin est rustique et légèrement amer : un blanc sec et fruité l'accompagne sans le contrarier.",
        biere: "En Bretagne on la boit avec du cidre ; à défaut, une bière blonde légère joue le même rôle."
      } },

    { id: 'galette_rois', etape: 'dessert', nom: 'Galette des rois et frangipane',
      mots: ['galette des rois', 'frangipane', 'pithiviers'],
      axes: { sucre: 2, gras: 2, puissance: 1.6 },
      familles: { moelleux: 88, effervescent: 74, champagne: 70, liquoreux: 72, vdn: 45 },
      raisons: {
        moelleux: "L'amande et le beurre appellent un moelleux frais : le sucre du vin doit rester en dessous de celui du gâteau.",
        effervescent: "Un crémant demi-sec est l'accord de janvier — les bulles allègent la frangipane."
      } },

    { id: 'baba', etape: 'dessert', nom: 'Baba au rhum et savarin',
      mots: ['baba au rhum', 'baba', 'savarin'],
      axes: { sucre: 2.5, puissance: 2 },
      familles: { rhum: 80, moelleux: 76, liquoreux: 70, vdn: 60, champagne: 45 },
      raisons: {
        rhum: "C'est le seul dessert qui réclame son spiritueux : le même rhum que celui du sirop, servi à part.",
        moelleux: "Un moelleux frais fait le lien entre le sirop et la crème sans ajouter de chaleur."
      } },

    { id: 'patisserie_creme', etape: 'dessert', nom: 'Pâtisseries à la crème',
      mots: ['saint honore', 'buche de noel', 'buche patissiere', 'cannoli', 'cannolo', 'paris brest', 'eclair', 'choux a la creme', 'mille feuille',
             'religieuse', 'chou chantilly', 'profiterole', 'trifle', 'tarte tropezienne', 'mont blanc', 'fraisier', 'charlotte', 'banoffee', 'zabaione', 'sabayon', 'sfogliatella', 'baba napolitain', 'crostata', 'crostata di ricotta', 'tarte a la ricotta'],
      axes: { sucre: 2.2, gras: 2, puissance: 1.6 },
      familles: { moelleux: 86, liquoreux: 80, champagne: 62, vdn: 58, effervescent: 60 },
      raisons: {
        moelleux: "La crème pâtissière est douce et grasse : un moelleux vif la relève, un liquoreux trop puissant l'écrase.",
        liquoreux: "Sur un Paris-Brest praliné, un liquoreux tient tête à la noisette et au beurre."
      } },

    { id: 'thon', etape: 'plat', nom: 'Thon et poissons de caractère',
      mots: ['thon', 'thon mi cuit', 'tataki', 'espadon', 'saumon mi cuit'],
      axes: { iode: 1.5, gras: 1.8, umami: 1.5, puissance: 1.8 },
      familles: { rose: 76, blanc_riche: 74, rouge: 60, blanc_sec: 70, effervescent: 35 },
      style: { rouge_leger: 0.9 },
      raisons: {
        rose: "Le thon a la texture d'une viande : un rosé de gastronomie ou un rouge très léger tiennent, là où un blanc fluet disparaît.",
        rouge: "Servi frais et sans tanin, un rouge léger est l'accord des ports de Méditerranée sur le thon grillé."
      } },

    { id: 'poisson_sauce_relevee', etape: 'plat', nom: 'Poisson en sauce relevée',
      mots: ['armoricaine', 'americaine', 'lotte a l armoricaine', 'bouillabaisse', 'sauce homardine', 'matelote'],
      axes: { iode: 2, gras: 2, epice: 0.8, puissance: 2 },
      familles: { blanc_riche: 90, rose: 70, blanc_sec: 68, champagne: 50, rouge: 20 },
      style: { blanc_riche: 0.9 },
      raisons: {
        blanc_riche: "Une sauce à base de crustacés est riche et safranée : elle demande un blanc ample, jamais un blanc maigre."
      } },

    /* --- Troisième passe du 2026-08-08 : les derniers trous relevés par
           le scan de 237 plats. Y compris quatre ressemblances franchement
           fausses que seul un mot exact pouvait chasser — « burrata » lu
           comme un burrito mexicain, « truite aux amandes » comme une
           salade, « crêpes sucrées » comme un plat de champignons. --- */

    { id: 'poisson_sale', etape: 'apero', nom: 'Poissons salés et fumés à l’apéritif',
      mots: ['hareng', 'anchois', 'poutargue', 'oeufs de poisson', 'sprat'],
      axes: { sel: 2.5, iode: 2, gras: 1.5, puissance: 1.4 },
      familles: { blanc_sec: 90, champagne: 86, effervescent: 78, vodka: 66, gin: 52, biere: 50, rose: 40, rouge: 5 },
      style: { blanc_vif: 1 },
      deconseille: { rouge: 'Le sel et l’iode du poisson salé rendent le tanin métallique : on reste sur un blanc vif.' },
      raisons: {
        blanc_sec: "Très salé et gras à la fois : seul un blanc franchement acide tient la distance."
      } },

    { id: 'poisson_blanc', etape: 'plat', nom: 'Poissons blancs et fritures',
      mots: ['morue', 'colin', 'merlan', 'raie', 'eglefin', 'haddock', 'lieu jaune', 'julienne',
             'poisson pane', 'friture de poisson', 'poisson', 'poissons', 'filet de poisson',
             'poisson blanc', 'plie', 'limande', 'carrelet', 'tacaud', 'bacalhau', 'bacalhau a bras', 'bacalao'],
      axes: { iode: 1.8, gras: 1.2, puissance: 1.1 },
      familles: { blanc_sec: 92, blanc_riche: 66, effervescent: 58, champagne: 48, rose: 42, rouge: 6 },
      style: { blanc_vif: 0.9 },
      raisons: {
        blanc_sec: "Chair blanche et discrète : un blanc sec et citronné apporte ce que le poisson n’a pas, sans le couvrir.",
        blanc_riche: "Au beurre noir ou en sauce, la même chair supporte un blanc plus rond."
      } },

    { id: 'truite', etape: 'plat', nom: 'Truite et poissons de rivière',
      mots: ['truite', 'truite aux amandes', 'omble', 'sandre', 'brochet', 'perche', 'feras', 'fera', 'fera du lac', 'coregone', 'lavaret'],
      axes: { iode: 1, gras: 1.4, puissance: 1.1 },
      familles: { blanc_sec: 90, blanc_riche: 72, effervescent: 52, rose: 38, rouge: 8 },
      style: { blanc_vif: 0.8 },
      raisons: {
        blanc_sec: "Le poisson de rivière est plus doux que celui de mer : un blanc sec et fin, sans bois, le laisse s’exprimer."
      } },

    { id: 'feuillete_sale', etape: 'apero', nom: 'Feuilletés et bouchées chaudes',
      mots: ['cake sale', 'bouchee a la reine', 'vol au vent', 'croquette', 'allumette au fromage',
             'mini quiche', 'pastilla'],
      axes: { gras: 2.2, sel: 1.5, puissance: 1.3 },
      familles: { effervescent: 84, champagne: 80, blanc_sec: 76, blanc_riche: 60, rose: 45 },
      raisons: {
        effervescent: "Le feuilletage est du beurre : les bulles le décrochent du palais, c’est toute la logique de l’apéritif."
      } },

    { id: 'mozzarella', etape: 'entree', nom: 'Mozzarella, burrata et fromages frais italiens',
      mots: ['burrata', 'mozzarella', 'tomate mozzarella', 'insalata caprese', 'salade caprese', 'tomate mozzarella', 'stracciatella', 'ricotta'],
      axes: { gras: 1.8, acide: 1.2, sel: 1, puissance: 1 },
      familles: { blanc_sec: 86, rose: 70, effervescent: 62, blanc_riche: 55, rouge: 25 },
      style: { blanc_vif: 0.8 },
      raisons: {
        blanc_sec: "La burrata est lactée et grasse, la tomate acide : un blanc vif tient les deux bouts.",
        rose: "Un rosé sec et fruité fait l’accord d’été, avec le basilic et l’huile d’olive."
      } },

    { id: 'abats', etape: 'plat', nom: 'Abats', registre: 2,
      mots: ['rognon', 'tripes', 'tripoux', 'abats', 'fraise de veau', 'coeur de veau', 'haggis', 'tete pressee', 'fromage de tete', 'jambon persille', 'tablier de sapeur', 'gras double', 'gras-double', 'callos', 'trippa', 'trippa alla romana', 'coda alla vaccinara', 'rabo de toro', 'queue de boeuf', 'tablier'],
      axes: { umami: 2.5, gras: 2, puissance: 2.2 },
      familles: { rouge: 88, blanc_sec: 45, rose: 30 },
      style: { rouge_puissant: 0.6 },
      raisons: {
        rouge: "Les abats sont puissants et ferreux : il faut un rouge de caractère, avec du fruit pour les adoucir."
      } },

    { id: 'potee', etape: 'plat', nom: 'Potée, garbure et petit salé',
      mots: ['potee', 'garbure', 'soupe au chou', 'petit sale', 'poule farcie', 'choucroute de la mer', 'jarret aux lentilles', 'jarret'],
      axes: { sel: 2, gras: 2, umami: 2, puissance: 1.8 },
      familles: { rouge: 84, blanc_sec: 55, rose: 40 },
      style: { rouge_leger: 0.8 },
      raisons: {
        rouge: "Un plat de bouillon salé et gras : un rouge simple, franc et servi frais, comme à la ferme."
      } },

    { id: 'grillades', etape: 'plat', nom: 'Brochettes, merguez et grillades',
      mots: ['brochette', 'merguez', 'chipolata', 'saucisse grillee', 'grillade', 'kebab', 'kefta'],
      axes: { fume: 2, gras: 2, epice: 1, puissance: 1.8 },
      familles: { rouge: 86, rose: 68, biere: 55, blanc_sec: 30 },
      style: { rouge_leger: 0.7 },
      raisons: {
        rouge: "La grillade appelle un rouge fruité et généreux, servi frais : le fumé du gril fait le reste.",
        rose: "En plein été, un rosé de caractère tient les merguez et les épices mieux qu’un rouge chaud."
      } },

    { id: 'crepes_sucrees', etape: 'dessert', nom: 'Crêpes et gaufres sucrées',
      mots: ['crepe sucree', 'crepes sucrees', 'crepe au sucre', 'crepe suzette', 'crepes suzette', 'suzette', 'gaufre', 'pancake', 'beignet sucre', 'churros', 'beignet de carnaval', 'beignets de carnaval'],
      supplante: ['asiatique_frit'],
      axes: { sucre: 2, gras: 1.5, puissance: 1.3 },
      familles: { moelleux: 82, effervescent: 68, liquoreux: 62, liqueur: 64, champagne: 55, vdn: 45 },
      raisons: {
        moelleux: "Sucre, beurre et citron : un moelleux frais suit la crêpe sans l’alourdir.",
        effervescent: "Un cidre ou un crémant demi-sec sont les compagnons traditionnels de la crêpe sucrée."
      } },

    { id: 'patisserie_seche', etape: 'dessert', nom: 'Pâtisseries sèches et petits gâteaux',
      mots: ['far breton', 'kouign amann', 'cannele', 'canele', 'madeleine', 'financier', 'cookie',
             'sable', 'tuile', 'biscuit', 'quatre quarts', 'scone', 'scones', 'clotted cream', 'tarte au sucre', 'gateau au yaourt', 'pain d epices', 'bugne', 'bugnes', 'oreillette', 'oreillettes'],
      supplante: ['epice'],
      axes: { sucre: 2, gras: 1.8, puissance: 1.4 },
      familles: { moelleux: 84, liquoreux: 76, vdn: 60, champagne: 50, effervescent: 52, the: 58, liqueur: 45 },
      raisons: {
        moelleux: "Beurre et sucre caramélisé : un moelleux du Sud-Ouest, frais et pas trop riche, est l’accord le plus sûr.",
        liquoreux: "Sur un cannelé ou un kouign-amann bien caramélisés, un liquoreux répond au sucre cuit."
      } },

    { id: 'dessert_glace', etape: 'dessert', nom: 'Desserts glacés',
      mots: ['banana split', 'coupe glacee', 'vacherin', 'omelette norvegienne', 'dame blanche',
             'glace', 'sorbet', 'nougat glace', 'parfait glace', 'affogato', 'semifreddo', 'gelato', 'granite', 'peche melba'],
      axes: { sucre: 2.2, puissance: 1.2 },
      familles: { moelleux: 84, liquoreux: 78, vdn: 74, effervescent: 66, champagne: 58, liqueur: 62, rhum: 55, punch: 58, jus: 45 },
      raisons: {
        moelleux: "Un dessert glacé anesthésie le palais : le vin doit être très parfumé et servi bien frais pour se faire entendre.",
        vdn: "Sur une glace vanille ou un banana split, un vin doux naturel apporte le fruit cuit qui manque au froid."
      } },

    /* ===================================================================
       QUATRIÈME PASSE — les 88 plats des recettes de la maison que la
       bibliothèque ne savait pas lire. Ils venaient de quatre mondes :
       la cuisine classique française (garnitures et entremets nommés
       d'après des personnages), les cuisines régionales, l'Italie et
       l'Asie. Sans eux, ces recettes retombaient sur le profil moyen de
       leur étape — et un moelleux se retrouvait sur un plat salé.
       =================================================================== */

    { id: 'entremets_classique', etape: 'dessert', nom: 'Entremets et soufflés classiques',
      mots: ['pouding', 'pudding', 'diplomate', 'nesselrode', 'souffle', 'riz a l imperatrice',
             'timbale', 'rothschild', 'bavarois', 'charlotte', 'cerises jubile', 'coupe jacques',
             'omelette norvegienne'],
      axes: { sucre: 2.2, gras: 1.8, puissance: 1.5 },
      familles: { moelleux: 86, liquoreux: 82, champagne: 58, vdn: 58, effervescent: 52 },
      raisons: {
        moelleux: "Les entremets classiques sont crémeux et parfumés : un moelleux frais les accompagne sans les alourdir.",
        liquoreux: "Sur un soufflé aux fruits confits ou au Grand Marnier, un liquoreux répond au sucre et à l'alcool de la préparation."
      } },

    /* ⚠️ ACCESSOIRE : une garniture accompagne, elle ne commande pas.
       « Poulet rôti et pommes de terre » se lisait à 57 % comme un plat
       de légumes et à 43 % seulement comme une volaille — la garniture
       décidait du vin. Le drapeau `accessoire` divise son poids dès
       qu'une vraie pièce est reconnue dans la phrase. */
    { id: 'garniture_legume', etape: 'plat', nom: 'Garnitures et légumes cuisinés', accessoire: true,
      mots: ['puree soubise', 'mornay', 'petits pois a la francaise', 'carottes vichy', 'chou fleur',
             'gratin de legumes', 'poireaux', 'endives', 'courgettes farcies', 'aubergines grillees',
             'pommes dauphine', 'pommes anna', 'pommes noisette', 'pommes de terre', 'gratin dauphinois',
             'legumes farcis', 'celeri remoulade', 'topinambour', 'topinambours', 'panais', 'salsifis', 'cardons', 'pommes sarladaises', 'sarladaises', 'stamppot', 'polenta'],
      axes: { gras: 1.2, umami: 1, puissance: 1 },
      familles: { blanc_sec: 80, blanc_riche: 66, rose: 58, rouge: 40, effervescent: 40 },
      raisons: {
        blanc_sec: "Un légume cuisiné au beurre ou à la crème demande de la fraîcheur : un blanc sec sans bois fait le travail."
      } },

    { id: 'sauce_vin', etape: 'plat', nom: 'Plats en sauce au vin', registre: 2,
      mots: ['sauce bordelaise', 'sauce bercy', 'marchand de vin', 'lamproie a la bordelaise',
             'lamproie', 'sauce au vin rouge', 'meurette', 'brasato', 'brasato al barolo', 'risotto al barolo', 'madere', 'sauce madere'],
      axes: { umami: 2.2, gras: 1.8, tanin: 1.5, puissance: 2.2 },
      familles: { rouge: 94, rose: 20, blanc_riche: 15 },
      style: { rouge_puissant: 0.6 },
      terroir: ['bordeaux', 'bergerac', 'pecharmant', 'saint emilion', 'castillon', 'fronsac', 'medoc'],
      raisons: {
        rouge: "On boit le vin de la sauce : un rouge de la même région, structuré, prolonge exactement ce qu'il y a dans l'assiette."
      } },

    { id: 'canard_sauce_fruit', etape: 'plat', nom: 'Canard aux fruits', registre: 2,
      mots: ['caneton', 'bigarade', 'rouennaise', 'canard a l orange', 'poulet a l orange',
             'canard aux cerises', 'porc au caramel', 'char siu', 'teriyaki', 'laque', 'magret aux peches', 'magret aux figues', 'canard aux figues', 'canard aux tamarins'],
      supplante: ['fruits'],
      axes: { sucre: 1.8, gras: 2, umami: 1.8, puissance: 2 },
      familles: { rouge: 78, vdn: 74, moelleux: 62, rose: 40 },
      style: { rouge_leger: 0.6 },
      raisons: {
        rouge: "Le sucré de la sauce adoucit le tanin : un rouge fruité, sans bois marqué, tient la viande et l'agrume.",
        vdn: "Un vin doux naturel fait écho au caramel et à l'orange — l'accord de sucre à sucre, plus rare mais très juste."
      } },

    { id: 'asie_thai', etape: 'plat', nom: 'Cuisine thaïe et du Sud-Est asiatique',
      mots: ['khao soi', 'khao pad', 'pad see ew', 'pad krapow', 'som tam', 'tom kha kai', 'tom yum',
             'curry vert', 'curry rouge', 'satay', 'nasi goreng', 'rendang', 'larb', 'mi quang', 'bun cha', 'cha ca'],
      axes: { epice: 2.2, sucre: 1.2, acide: 1.5, umami: 2, puissance: 1.8 },
      /* Le moelleux passe sous le seuil du plat principal : il reste une
         option sur un curry très pimenté, jamais la recommandation sur
         un riz sauté. */
      familles: { moelleux: 45, blanc_sec: 84, rose: 68, biere: 64, effervescent: 60, rouge: 22 },
      style: { blanc_vif: 0.6 },
      raisons: {
        blanc_sec: "Piment, citronnelle et sucre : un blanc aromatique et un peu tendre calme le feu sans effacer les parfums.",
        moelleux: "Sur un curry franchement relevé, une pointe de sucre dans le verre éteint le piment mieux que l'acidité."
      } },

    { id: 'asie_coree', etape: 'plat', nom: 'Cuisine coréenne',
      mots: ['dak galbi', 'samgyeopsal', 'pajeon', 'sundubu', 'kimbap', 'galbi', 'kimchi',
             'tteokbokki', 'japchae', 'bibimbap', 'bulgogi', 'jjigae'],
      axes: { epice: 2, fume: 1.5, umami: 2.5, acide: 1.2, puissance: 2 },
      familles: { rouge: 66, rose: 64, biere: 70, blanc_sec: 62, effervescent: 50 },
      style: { rouge_leger: 0.9 },
      raisons: {
        rouge: "Grillades et pâte de piment : un rouge léger servi frais, sans tanin, passe là où un vin de garde brûlerait.",
        biere: "Le kimchi et le gochujang appellent une bière fraîche : c'est l'accord du pays."
      } },

    { id: 'asie_japon', etape: 'plat', nom: 'Cuisine japonaise',
      mots: ['yakisoba', 'karaage', 'takoyaki', 'katsudon', 'yakitori', 'okonomiyaki', 'gyudon',
             'oyakodon', 'onigiri', 'tonkatsu', 'donburi', 'tempura', 'unagi', 'shabu shabu', 'sukiyaki', 'chirashi'],
      axes: { umami: 2.5, sel: 1.8, sucre: 1, puissance: 1.6 },
      familles: { blanc_sec: 74, effervescent: 68, champagne: 60, biere: 62, rose: 55, rouge: 40 },
      style: { blanc_vif: 0.7 },
      raisons: {
        blanc_sec: "Le sucré-salé du soja demande de la fraîcheur et de la tension : un blanc sec, jamais boisé.",
        effervescent: "Sur les fritures japonaises, les bulles font le même travail que sur un beignet français."
      } },

    { id: 'asie_chine', etape: 'plat', nom: 'Cuisine chinoise et dim sum',
      mots: ['siu mai', 'har gow', 'xiao long bao', 'mapo tofu', 'dan dan', 'sichuan', 'sichuanaise',
             'dim sum', 'canard laque', 'boeuf saute', 'wok'],
      axes: { epice: 1.8, umami: 2.5, sel: 1.8, gras: 1.5, puissance: 1.8 },
      familles: { blanc_sec: 70, rose: 62, effervescent: 62, rouge: 55, biere: 58, moelleux: 45 },
      style: { rouge_leger: 0.8 },
      raisons: {
        blanc_sec: "Les vapeurs et le soja sont salins : un blanc vif nettoie et relance l'appétit entre deux bouchées.",
        rouge: "Sur un plat sichuanais gras et pimenté, un rouge léger servi frais tient mieux qu'un blanc trop fluet."
      } },

    { id: 'patisserie_asie', etape: 'dessert', nom: 'Desserts asiatiques',
      mots: ['mochi', 'dorayaki', 'sticky rice', 'mango sticky rice', 'perles de coco', 'daifuku',
             'fiadone', 'brocciu'],
      axes: { sucre: 2, puissance: 1.2 },
      familles: { moelleux: 86, liquoreux: 74, vdn: 66, the: 62, effervescent: 58, punch: 52, champagne: 48 },
      raisons: {
        moelleux: "Riz gluant, haricot rouge ou coco : des sucres doux, qui appellent un vin doux léger et très frais."
      } },

    { id: 'italie_tomate', etape: 'plat', nom: 'Cuisine italienne à la tomate',
      mots: ['minestrone', 'alla norma', 'pasta alla norma', 'panzanella', 'polpette', 'arancini', 'bruschetta', 'focaccia',
             'parmigiana', 'arrabbiata', 'saltimbocca', 'caponata', 'osso buco', 'boulettes', 'provencale'],
      axes: { acide: 1.8, umami: 2, sel: 1.5, gras: 1.5, puissance: 1.7 },
      familles: { rouge: 84, rose: 62, blanc_sec: 55, effervescent: 35 },
      style: { rouge_leger: 0.8 },
      raisons: {
        rouge: "La tomate cuite est acide : elle veut un rouge fruité et souple, jamais un vin tannique qui deviendrait dur.",
        rose: "Sur une panzanella ou une focaccia, un rosé sec et vif est plus juste qu'un rouge."
      } },

    { id: 'aioli_ail', etape: 'plat', nom: 'Aïoli, anchoïade et plats à l’ail',
      mots: ['aioli', 'anchoiade', 'tourin', 'soupe a l ail', 'bagna cauda', 'rouille'],
      axes: { sel: 2, gras: 2, puissance: 1.6 },
      familles: { blanc_sec: 86, rose: 74, effervescent: 50, rouge: 20 },
      style: { blanc_vif: 0.9 },
      raisons: {
        blanc_sec: "L'ail et l'anchois sont puissants et gras : un blanc très frais et salin les tient, un rouge s'y perdrait.",
        rose: "Un rosé de Provence sec est l'accord traditionnel du grand aïoli."
      } },

    { id: 'plat_fromage_region', etape: 'plat', nom: 'Plats régionaux au fromage',
      mots: ['welsh', 'morbiflette', 'spaetzle', 'baeckeoffe', 'croziflette', 'tartiflette au morbier'],
      axes: { gras: 2.5, sel: 2, umami: 2, puissance: 1.8 },
      familles: { blanc_sec: 82, rouge: 62, biere: 55, rose: 40 },
      style: { blanc_vif: 0.7 },
      raisons: {
        blanc_sec: "Le fromage fondu tapisse la bouche : c'est l'acidité d'un blanc sec qui la libère, pas la puissance d'un rouge."
      } },

    { id: 'soupe_poisson', etape: 'plat', nom: 'Soupes et potées de la mer',
      mots: ['cotriade', 'soupe de poisson', 'chaudree', 'waterzooi', 'petite marmite', 'zarzuela', 'suquet', 'suquet de peix', 'fideua', 'bourride', 'ttoro', 'thieboudienne', 'waterzooi de poisson', 'bouillinade', 'zuppa di pesce', 'arroz de marisco', 'laksaus', 'soupe de saumon'],
      axes: { iode: 2.2, sel: 1.8, gras: 1.5, puissance: 1.6 },
      familles: { blanc_sec: 88, blanc_riche: 74, rose: 55, effervescent: 45, rouge: 10 },
      raisons: {
        blanc_sec: "Un bouillon marin appelle un blanc sec et salin, dans le prolongement direct de l'assiette."
      } },

    { id: 'kouglof', etape: 'dessert', nom: 'Kouglof, brioches et gâteaux levés',
      mots: ['kouglof', 'brioche', 'panettone', 'stollen', 'gateau leve', 'colomba'],
      axes: { sucre: 1.8, gras: 1.8, puissance: 1.4 },
      familles: { moelleux: 86, effervescent: 66, liquoreux: 70, champagne: 55, vdn: 50 },
      raisons: {
        moelleux: "Un gâteau levé, beurré et peu sucré : un moelleux d'Alsace ou du Sud-Ouest, frais, y est chez lui."
      } },

    { id: 'patisserie_miel', etape: 'dessert', nom: 'Pâtisseries au miel et aux fruits secs',
      mots: ['baklava', 'nougat', 'halva', 'makroud', 'corne de gazelle', 'pastilla sucree'],
      axes: { sucre: 2.8, gras: 2, puissance: 1.8 },
      familles: { liquoreux: 88, vdn: 82, moelleux: 74, champagne: 40 },
      raisons: {
        liquoreux: "Miel, noix et fleur d'oranger : il faut un vin plus sucré que le dessert, sinon il paraît maigre.",
        vdn: "Un vin doux naturel de type Rivesaltes ou Maury tient tête au miel et aux fruits secs."
      } },

    { id: 'dolma', etape: 'apero', nom: 'Feuilles de vigne et bouchées grecques',
      mots: ['dolmadakia', 'dolma', 'feuille de vigne', 'feuilles de vigne', 'spanakopita', 'tiropita'],
      axes: { acide: 1.5, sel: 1.5, gras: 1.2, puissance: 1.1 },
      familles: { blanc_sec: 84, rose: 70, effervescent: 55, rouge: 25 },
      style: { blanc_vif: 0.9 },
      raisons: {
        blanc_sec: "Citron, huile d'olive et herbes : un blanc sec et vif prolonge l'assiette sans rien ajouter."
      } },

    /* =================================================================
       CUISINES DU MONDE — deuxième vague (v6.0)
       -----------------------------------------------------------------
       Les 84 premiers archétypes couvraient la table française et ses
       voisines. Un relevé de 285 plats du monde en laissait 39 sans
       aucune lecture, et huit rattachés par simple ressemblance — dont
       une bûche de Noël servie comme du GIBIER (« bûche » ≈ « biche »).
       Un plat sans lecture n'est pas neutre : il retombe sur le réglage
       par défaut de son moment de repas, c'est-à-dire un rouge sur tout
       ce qui est un plat. D'où un Pomerol sur un butter chicken et un
       Pomerol sur du tofu grillé.
       Ce qui suit ferme ces trous. Chaque entrée est écrite comme un
       sommelier la dirait : la structure du plat d'abord, la famille de
       vin ensuite, et la raison en toutes lettres.
       ================================================================= */

    /* ---------------------- FUMÉ & BARBECUE AMÉRICAIN ---------------- */
    { id: 'bbq_fume', etape: 'plat', nom: 'Viandes fumées au barbecue', registre: 2,
      mots: ['brisket', 'pulled pork', 'travers de porc', 'ribs', 'spare ribs', 'porc effiloche',
             'barbecue americain', 'pastrami', 'poitrine fumee', 'echine fumee', 'smoked'],
      axes: { fume: 3, gras: 2.2, sucre: 1.5, umami: 2, puissance: 2.6, sel: 1.5 },
      familles: { rouge: 92, biere: 78, rose: 40, blanc_riche: 30, blanc_sec: 12, effervescent: 15 },
      style: { rouge_puissant: 1.2, biere_forte: 1 },
      raisons: {
        rouge: "Une viande longuement fumée, sa sauce sucrée et son gras : il faut un rouge charnu, un peu boisé, assez chaleureux pour tenir la fumée sans se durcir.",
        biere: "Une brune ou une ambrée un peu torréfiée répond à la fumaison comme un miroir — l'accord d'origine, et souvent le plus juste.",
        rose: "Sur des travers plus légers, un rosé de repas bien mûr passe la sauce sans lutter avec elle."
      } },

    /* ---------------------- CURRYS DOUX & CRÉMEUX -------------------- */
    /* À ne pas confondre avec `epice` : un butter chicken n'est pas un
       vindaloo. Ici la crème et les épices douces dominent, le piment
       est en retrait — c'est un plat GRAS et PARFUMÉ, pas un plat qui
       brûle. Le tanin y est un contresens : il s'accroche à la crème et
       durcit sur la cardamome. */
    { id: 'curry_doux', etape: 'plat', nom: 'Currys doux et crémeux', registre: 1,
      mots: ['butter chicken', 'laksa', 'tikka masala', 'poulet tikka', 'korma', 'massaman', 'curry de coco',
             'curry coco', 'curry doux', 'poulet au curry', 'curry de poulet', 'malai', 'pasanda',
             'curry de legumes', 'curry vegetarien', 'dal makhani', 'palak paneer', 'paneer', 'saag gosht', 'saag', 'chana masala', 'idli', 'sambar', 'dosa', 'khao soi', 'kefta mkaouara'],
      axes: { gras: 2.5, epice: 1.5, umami: 1.8, sucre: 1, puissance: 1.8 },
      familles: { blanc_riche: 88, moelleux: 74, blanc_sec: 72, rose: 66, effervescent: 52, biere: 55, rouge: 22 },
      style: { blanc_riche: 1 },
      deconseille: { rouge: 'Le tanin s’accroche à la crème et durcit sur les épices douces : le rouge est ici le seul vrai faux pas.' },
      raisons: {
        blanc_riche: "Un blanc ample et parfumé épouse la crème de coco et porte la cardamome : c'est l'accord de texture que demande ce plat.",
        moelleux: "Un soupçon de sucre résiduel tempère les épices et fait ressortir le fruit — la solution classique sur toute la cuisine indienne douce.",
        rose: "Un rosé mûr, sans tanin, passe partout sur un curry : c'est le choix sûr quand la table partage plusieurs plats."
      } },

    /* ---------------------- PANÉS & FRITURES DE VIANDE --------------- */
    { id: 'pane_frit', etape: 'plat', nom: 'Escalopes panées et fritures',
      mots: ['schnitzel', 'wiener schnitzel', 'escalope milanaise', 'milanaise', 'cordon bleu',
             'escalope panee', 'katsu', 'tonkatsu', 'katsu curry', 'poulet frit', 'fried chicken',
             'poulet pane', 'nuggets', 'poulet frit coreen', 'fish and chips', 'shepherd s pie', 'welsh', 'cotoletta', 'cotoletta alla milanese', 'raggmunk'],
      axes: { gras: 2.2, sel: 1.5, acide: 1.2, puissance: 1.5 },
      familles: { blanc_sec: 86, effervescent: 80, biere: 74, rose: 68, champagne: 62, rouge: 38 },
      style: { blanc_vif: 1.1 },
      raisons: {
        blanc_sec: "La panure est grasse et salée : il lui faut de l'acidité, celle-là même qu'apporte le quartier de citron posé sur l'assiette.",
        effervescent: "Les bulles nettoient la friture à chaque gorgée — sur tout ce qui est pané, c'est l'accord le plus sûr.",
        biere: "Une blonde bien sèche et amère fait le même travail que le citron, en plus désaltérant."
      } },

    /* ---------------------- POULPE, SEICHE, CALAMAR ------------------ */
    { id: 'poulpe', etape: 'entree', nom: 'Poulpe, calamar et seiche',
      mots: ['poulpe', 'pieuvre', 'calamar', 'calmar', 'encornet', 'seiche', 'chipiron',
             'tentacule', 'poulpe a la galicienne', 'pulpo', 'calamars frits', 'chipirons', 'tielle', 'tielle setoise', 'arroz negro', 'chipirones', 'encornets farcis', 'seiche a la rouille'],
      axes: { iode: 2.5, sel: 2, gras: 1.2, fume: 1, puissance: 1.5 },
      familles: { blanc_sec: 92, rose: 74, effervescent: 66, champagne: 60, blanc_riche: 58, rouge: 18 },
      style: { blanc_vif: 1 },
      deconseille: { rouge: 'La chair iodée et le tanin donnent la même amertume métallique que sur l’huître.' },
      raisons: {
        blanc_sec: "Chair iodée, huile d'olive et paprika : un blanc salin et droit tient tout l'ensemble sans l'alourdir.",
        rose: "Grillé à la plancha, le poulpe prend des notes fumées qu'un rosé de gastronomie accompagne très bien."
      } },

    /* ---------------------- SOUPES FROIDES --------------------------- */
    /* Séparé de `soupe` : un gazpacho est ACIDE et CRU, un velouté est
       gras et chaud. Les rattacher ensemble faisait servir un blanc
       ample sur de la tomate crue et du vinaigre. */
    { id: 'soupe_froide', etape: 'entree', nom: 'Soupes froides',
      mots: ['gazpacho', 'gaspacho', 'vichyssoise', 'soupe froide', 'soupe glacee', 'salmorejo',
             'ajo blanco', 'veloute froid'],
      axes: { acide: 2.5, sel: 1.5, gras: 1, puissance: 1 },
      familles: { rose: 86, blanc_sec: 84, effervescent: 70, champagne: 55, rouge: 8 },
      style: { blanc_vif: 1.2, rose_vif: 1 },
      deconseille: { rouge: 'Tomate crue et vinaigre : le rouge y devient dur et métallique.' },
      raisons: {
        rose: "Un rosé très sec et frais suit la tomate crue et l'huile d'olive sans jamais peser — l'accord d'été par excellence.",
        blanc_sec: "Il faut au vin autant d'acidité qu'au plat, sinon il paraît mou : un blanc tendu, servi très frais."
      } },

    /* ---------------------- CAVIAR & ŒUFS DE POISSON ----------------- */
    { id: 'caviar', etape: 'apero', nom: 'Caviar et œufs de poisson', registre: 3,
      mots: ['caviar', 'oeufs de saumon', 'oeuf de saumon', 'keta', 'poutargue', 'boutargue',
             'tarama', 'blinis', 'oeufs de truite'],
      axes: { iode: 3, sel: 3, gras: 2, puissance: 1.5 },
      familles: { champagne: 96, effervescent: 76, blanc_sec: 74, vodka: 70, rouge: 2 },
      style: { blanc_vif: 1 },
      deconseille: { rouge: 'Aucun rouge ne tient devant le sel iodé du caviar : il tourne au métal.' },
      raisons: {
        champagne: "Un blanc de blancs peu dosé est le seul accord que la tradition retient : la craie du chardonnay et le sel iodé se répondent, les bulles allègent le gras.",
        vodka: "L'accord russe, à la lettre : une vodka glacée nettoie le palais entre deux cuillères. C'est un accord d'apéritif, jamais de table.",
        blanc_sec: "À défaut de bulles, un blanc très minéral et peu aromatique — surtout pas boisé, qui écraserait tout."
      } },

    /* ---------------------- POISSONS MARINÉS DU NORD ----------------- */
    { id: 'hareng_marine', etape: 'apero', nom: 'Harengs et poissons marinés',
      mots: ['hareng', 'harengs marines', 'rollmops', 'anchois marines', 'escabeche',
             'maquereau au vin blanc', 'ceviche', 'poisson marine', 'gravlax', 'sarde in saor', 'matjes', 'sardines marinees'],
      axes: { acide: 2.5, sel: 2.5, iode: 2, puissance: 1.3 },
      familles: { blanc_sec: 90, effervescent: 74, champagne: 68, biere: 55, vodka: 50, rose: 45, rouge: 4 },
      style: { blanc_vif: 1.3 },
      deconseille: { rouge: 'La marinade au vinaigre casse net un vin rouge.' },
      raisons: {
        blanc_sec: "Contre une marinade acide, il faut un vin au moins aussi acide : un blanc tendu et sec, sans aucun bois.",
        biere: "Une blonde sèche du Nord fait ce que fait la bière avec le hareng depuis toujours : elle rince et rafraîchit."
      } },

    /* ---------------------- PAPRIKA & CUISINE DE L'EST --------------- */
    { id: 'paprika_est', etape: 'plat', nom: 'Goulash, bortsch et cuisine de l’Est', registre: 1,
      mots: ['goulash', 'goulasch', 'gulyas', 'bortsch', 'borchtch', 'paprikash', 'porkolt',
             'boeuf strogonoff', 'stroganoff', 'chou farci'],
      axes: { gras: 2, umami: 2, epice: 1.2, sucre: 1, puissance: 2.2 },
      familles: { rouge: 88, rose: 50, blanc_riche: 42, biere: 45, blanc_sec: 20 },
      style: { rouge_leger: 0.7 },
      raisons: {
        rouge: "Le paprika demande du fruit et peu de tanin : un rouge souple, épicé, chaleureux — jamais un vin de garde austère qui durcirait sur le poivron.",
        rose: "Sur un bortsch ou un chou farci, un rosé de caractère tient le gras et la douceur de la betterave."
      } },

    /* ---------------------- PÂTES FARCIES & RAVIOLES ----------------- */
    { id: 'ravioles', etape: 'plat', nom: 'Ravioles, pierogi et pâtes farcies',
      mots: ['pierogi', 'raviole', 'ravioles', 'ravioli', 'tortellini', 'agnolotti',
             'gnocchi', 'knodel', 'maultaschen', 'fleischnacka', 'schupfnudeln', 'spaetzle'],
      axes: { gras: 2, umami: 1.5, sel: 1.3, puissance: 1.5 },
      familles: { blanc_sec: 80, blanc_riche: 76, rouge: 62, rose: 58, effervescent: 52 },
      raisons: {
        blanc_sec: "Une farce et du beurre : un blanc à la fois rond et vif fait le lien, sans le tanin qui durcirait la pâte.",
        rouge: "Si la farce est à la viande ou aux champignons, un rouge léger et fruité passe très bien."
      } },

    /* ---------------------- SAUCISSES & BRASSERIE -------------------- */
    { id: 'saucisse_brasserie', etape: 'plat', nom: 'Saucisses et plats de brasserie',
      mots: ['knack', 'knacks', 'bretzel', 'currywurst', 'saucisse blanche', 'weisswurst',
             'francfort', 'hot dog', 'hot-dog', 'saucisse de strasbourg', 'bratwurst'],
      axes: { sel: 2.2, gras: 2, umami: 1.5, puissance: 1.5 },
      familles: { biere: 90, blanc_sec: 76, rose: 60, rouge: 50, effervescent: 48 },
      style: { blanc_vif: 0.8 },
      raisons: {
        biere: "C'est l'accord d'origine et il n'a jamais été détrôné : une blonde franche lave le sel et le gras de la saucisse.",
        blanc_sec: "Un blanc sec et vif — un riesling, un sylvaner — fait exactement le même travail avec plus de finesse."
      } },

    /* ---------------------- HARICOTS & PLATS DE POT ------------------ */
    { id: 'haricots_mijotes', etape: 'plat', nom: 'Plats de haricots et de légumes secs',
      mots: ['feijoada', 'fabada', 'chili con carne', 'haricots blancs', 'mogettes',
             'lentilles au petit sale', 'dal', 'daal', 'soupe de haricots', 'gigot de haricots'],
      axes: { gras: 2, umami: 2.2, sel: 2, puissance: 2.2 },
      familles: { rouge: 88, rose: 48, biere: 50, blanc_riche: 40, blanc_sec: 22 },
      style: { rouge_puissant: 0.8 },
      raisons: {
        rouge: "Un plat lourd et salé appelle un rouge de caractère, assez tannique pour trancher dans le gras du lard et du chorizo.",
        biere: "Sur un chili, une ambrée un peu douce calme le piment mieux qu'un vin."
      } },

    /* ---------------------- RIZ ET NOUILLES SAUTÉS ------------------- */
    /* Sans cette entrée, « nouilles sautées » tombait sur l'AÏOLI par
       ressemblance, et « riz cantonais » sur rien du tout. */
    { id: 'wok', etape: 'plat', nom: 'Riz et nouilles sautés au wok',
      mots: ['riz cantonais', 'riz saute', 'nasi goreng', 'fried rice', 'nouilles sautees',
             'nouilles saute', 'chow mein', 'yakisoba', 'pad see ew', 'lo mein', 'mie goreng',
             'wok de legumes', 'wok'],
      axes: { umami: 2.2, sel: 2, gras: 1.5, epice: 0.8, puissance: 1.4 },
      familles: { blanc_sec: 80, rose: 74, effervescent: 62, biere: 68, moelleux: 55, rouge: 30 },
      style: { blanc_vif: 0.9 },
      raisons: {
        blanc_sec: "Le soja apporte du sel et de l'umami : un blanc sec et fruité, sans bois, garde l'assiette vive.",
        rose: "Un rosé sec passe sur tout un wok partagé, viandes et légumes mêlés — c'est le vin des tables où l'on pioche.",
        biere: "Une lager fraîche est ce qu'on boit sur place, et pour de bonnes raisons : elle ne discute pas avec le soja."
      } },

    /* ---------------------- ROULEAUX FRAIS --------------------------- */
    { id: 'rouleau_frais', etape: 'apero', nom: 'Rouleaux de printemps et bouchées fraîches',
      mots: ['rouleau de printemps', 'rouleaux de printemps', 'summer roll', 'goi cuon',
             'edamame', 'salade de papaye', 'som tam'],
      axes: { acide: 1.8, iode: 1, sel: 1.2, puissance: 0.8 },
      familles: { blanc_sec: 88, effervescent: 74, rose: 72, champagne: 60, moelleux: 50, rouge: 6 },
      style: { blanc_vif: 1.2 },
      raisons: {
        blanc_sec: "Menthe, coriandre, crevette et sauce nuoc-mâm : un blanc très aromatique et vif suit les herbes sans les couvrir."
      } },

    /* ---------------------- TOFU & VÉGÉTAL ASIATIQUE ----------------- */
    { id: 'tofu', etape: 'plat', nom: 'Tofu et cuisine végétale asiatique',
      mots: ['tofu', 'tempeh', 'seitan', 'mapo tofu', 'tofu grille', 'tofu saute', 'mapo doufu', 'japchae'],
      axes: { umami: 2, sel: 1.5, epice: 0.8, puissance: 1 },
      familles: { blanc_sec: 84, rose: 70, effervescent: 58, moelleux: 48, biere: 55, rouge: 22 },
      style: { blanc_vif: 0.9 },
      deconseille: { rouge: 'Le tofu n’a ni gras ni sang à opposer au tanin : un rouge de garde l’efface complètement.' },
      raisons: {
        blanc_sec: "Le tofu ne porte pas le vin, c'est sa sauce qui décide : un blanc sec et parfumé accompagne le soja et le sésame sans écraser une matière très douce."
      } },

    /* ---------------------- GRATINS DE PÂTES AU FROMAGE -------------- */
    { id: 'gratin_fromage_pates', etape: 'plat', nom: 'Gratins de pâtes au fromage',
      mots: ['mac and cheese', 'macaroni au fromage', 'gratin de pates', 'gratin de macaroni',
             'pates au fromage', 'coquillettes au jambon'],
      axes: { gras: 3, sel: 2, umami: 2, puissance: 1.6 },
      familles: { blanc_sec: 84, blanc_riche: 80, effervescent: 74, biere: 60, rose: 55, rouge: 40 },
      style: { blanc_vif: 1 },
      raisons: {
        blanc_sec: "Béchamel et fromage fondu saturent le palais : c'est l'acidité d'un blanc vif qui remet l'assiette en marche, pas le tanin d'un rouge.",
        effervescent: "Les bulles font ici tout le travail : elles coupent le gras à chaque gorgée."
      } },

    /* ---------------------- TARTES AUX POMMES & STRUDEL -------------- */
    { id: 'tarte_pomme', etape: 'dessert', nom: 'Tartes aux pommes et strudel',
      mots: ['strudel', 'tarte aux pommes', 'apple pie', 'chausson aux pommes', 'tarte normande',
             'crumble aux pommes', 'tarte aux poires', 'tarte aux abricots'],
      axes: { sucre: 2.5, gras: 1.5, acide: 1.2, puissance: 1.2 },
      familles: { moelleux: 92, liquoreux: 88, effervescent: 62, calvados: 70, champagne: 48, vdn: 55 },
      raisons: {
        moelleux: "Un moelleux aux notes de pomme cuite et de coing double le dessert au lieu de lui répondre — et sa fraîcheur l'empêche d'être écœurant.",
        calvados: "Le calvados est le prolongement naturel de la pomme cuite : à servir après, en digestif, jamais dans le verre du dessert.",
        liquoreux: "Sur une tarte bien caramélisée, un liquoreux tient le sucre et apporte l'abricot confit."
      } },

    /* ---------------------- NOIX, CARAMEL, PÉCAN -------------------- */
    { id: 'dessert_noix', etape: 'dessert', nom: 'Desserts aux noix et au caramel', registre: 2,
      mots: ['pecan pie', 'tarte aux noix', 'tarte aux pecans', 'tarte au caramel', 'caramel beurre sale',
             'nougat', 'praline', 'tarte aux noisettes', 'baklava', 'gateau aux noix', 'banoffee pie'],
      axes: { sucre: 3, gras: 2, amer: 1, puissance: 2 },
      familles: { liquoreux: 92, vdn: 88, moelleux: 78, armagnac: 66, whisky: 50, champagne: 20 },
      raisons: {
        liquoreux: "Noix et caramel appellent le rancio et l'abricot confit d'un liquoreux mûr : c'est l'accord de sucre à sucre, celui qui ne laisse pas le vin maigre.",
        vdn: "Un vin doux naturel oxydatif — Rivesaltes, Maury — a exactement les notes de noix et de caramel du dessert.",
        armagnac: "Après le dessert, un armagnac reprend la noix et le pruneau : le digestif du Sud-Ouest sur la tarte du Sud-Ouest."
      } },

    /* ---------------------- FLANS & PÂTISSERIES À L'ŒUF ------------- */
    { id: 'flan_oeuf', etape: 'dessert', nom: 'Flans, pastéis et crèmes à l’œuf',
      mots: ['pasteis de nata', 'pastel de nata', 'flan', 'flan patissier', 'creme renversee',
             'creme caramel', 'quatre quarts', 'gateau basque', 'clafoutis'],
      axes: { sucre: 2.5, gras: 2, puissance: 1.3 },
      familles: { moelleux: 90, liquoreux: 84, vdn: 72, champagne: 45, effervescent: 50 },
      raisons: {
        moelleux: "Une crème à l'œuf est douce et grasse sans être puissante : un moelleux frais lui suffit, un liquoreux la couvrirait presque.",
        vdn: "Sur une pâtisserie caramélisée au four, un muscat apporte le raisin frais qui manque au dessert."
      } },

    /* ---------------------- CONFISERIES ----------------------------- */
    { id: 'confiserie', etape: 'dessert', nom: 'Confiseries et douceurs',
      mots: ['loukoum', 'nougat blanc', 'calisson', 'pate de fruit', 'guimauve', 'dragee',
             'chocolat au lait', 'bonbon', 'mochi'],
      axes: { sucre: 3, puissance: 1 },
      familles: { vdn: 86, moelleux: 82, liquoreux: 78, punch: 55, liqueur: 52, the: 48, champagne: 30 },
      raisons: {
        vdn: "Une confiserie est du sucre pur : seul un vin plus sucré qu'elle ne paraîtra pas acide et maigre à côté.",
        moelleux: "Servi bien frais, un moelleux garde de la fraîcheur là où la confiserie n'en a aucune."
      } },

    /* ---------------------- CIGARE ---------------------------------- */
    { id: 'cigare', etape: 'digestif', nom: 'Cigare', registre: 3,
      mots: ['cigare', 'havane', 'cigarillo'],
      axes: { fume: 3, amer: 2, puissance: 3 },
      familles: { rhum: 92, armagnac: 90, whisky: 88, cognac: 86, vdn: 74, calvados: 60, tequila: 56, eau_de_vie: 55 },
      raisons: {
        rhum: "Un rhum vieux et sa douceur de canne répondent au tabac : c'est l'accord cubain, et le plus généreux.",
        armagnac: "Le rancio de l'armagnac et la fumée du cigare tiennent la même longueur en bouche.",
        whisky: "Un single malt tourbé prolonge la fumée au lieu de la contrarier."
      } },

    /* ---------------------- SANDWICHS & EN-CAS --------------------- */
    { id: 'sandwich', etape: 'plat', nom: 'Sandwichs et en-cas',
      mots: ['sandwich', 'club sandwich', 'sandwich club', 'panini', 'bagel', 'wrap',
             'jambon beurre', 'kebab', 'pita', 'pan bagnat', 'sabich', 'smorrebrod', 'smørrebrød', 'tartine nordique', 'francesinha', 'croque monsieur', 'croque-monsieur', 'croque'],
      axes: { sel: 1.8, gras: 1.8, puissance: 1.2 },
      familles: { biere: 78, blanc_sec: 74, rose: 74, effervescent: 55, rouge: 48 },
      raisons: {
        rose: "Un sandwich se boit frais et sans cérémonie : un rosé sec ou une bière font mieux qu'un vin sérieux."
      } },

    /* ---------------------- LÉGUMES MARINÉS & ANTIPASTI ------------ */
    { id: 'antipasti', etape: 'apero', nom: 'Antipasti et légumes marinés',
      mots: ['antipasti', 'antipasto', 'poivrons marines', 'legumes marines', 'aubergines grillees',
             'artichauts a l huile', 'tomates sechees', 'giardiniera', 'olives marinees', 'pimientos de padron', 'padron', 'friggitelli'],
      axes: { acide: 2, sel: 2, gras: 1.5, amer: 1, puissance: 1.1 },
      familles: { blanc_sec: 84, rose: 82, effervescent: 66, rouge: 40, champagne: 50 },
      style: { blanc_vif: 1, rose_vif: 0.8 },
      raisons: {
        rose: "Huile d'olive, vinaigre et herbes : un rosé sec traverse toute la table d'antipasti sans jamais buter.",
        blanc_sec: "Il faut de l'acidité pour tenir la marinade — un blanc vif, et surtout pas boisé."
      } },

    /* ---------------------- VEAU FROID & SAUCES FROIDES ------------ */
    { id: 'viande_froide_sauce', etape: 'entree', nom: 'Viandes froides en sauce', registre: 2,
      mots: ['vitello tonnato', 'veau froid', 'boeuf froid', 'roti froid', 'langue sauce piquante',
             'cervelas remoulade', 'museau vinaigrette', 'gribiche', 'ravigote', 'remoulade'],
      axes: { gras: 2, acide: 1.8, sel: 1.5, umami: 1.5, puissance: 1.4 },
      familles: { blanc_sec: 86, rose: 74, blanc_riche: 70, effervescent: 60, rouge: 35 },
      style: { blanc_vif: 0.9 },
      raisons: {
        blanc_sec: "Une sauce montée au thon et aux câpres est grasse et acide à la fois : c'est un blanc vif et un peu rond qu'il faut, jamais un rouge."
      } },

    /* ---------------------- FRITURES DE MER ------------------------ */
    { id: 'fritto_misto', etape: 'apero', nom: 'Fritures de la mer',
      mots: ['fritto misto', 'friture de la mer', 'petite friture', 'beignets de crevette',
             'calamars a la romaine', 'tempura', 'acras', 'accras', 'eperlan', 'eperlans', 'friture d eperlans', 'fritto di paranza', 'supli', 'suppli'],
      axes: { gras: 2.2, iode: 2, sel: 2, puissance: 1.3 },
      familles: { effervescent: 90, blanc_sec: 88, champagne: 82, biere: 66, rose: 55, rouge: 5 },
      style: { blanc_vif: 1.2 },
      raisons: {
        effervescent: "Rien ne nettoie une friture comme des bulles sèches : c'est mécanique, et c'est imparable.",
        blanc_sec: "À défaut de bulles, un blanc très vif joue le rôle du citron qu'on presse dessus."
      } },

    /* ---------------------- TORTILLA & OMELETTES ------------------- */
    { id: 'omelette', etape: 'entree', nom: 'Omelettes et tortillas',
      mots: ['omelette', 'tortilla espanola', 'tortilla', 'frittata', 'oeufs brouilles',
             'oeufs benedicte', 'oeuf au plat', 'brouillade', 'souffle au fromage', 'souffle au comte', 'souffle au parmesan', 'souffle sale', 'souffle au crabe'],
      supplante: ['entremets_classique'],
      axes: { gras: 2, umami: 1.2, puissance: 1.1 },
      familles: { blanc_sec: 84, effervescent: 70, rose: 66, blanc_riche: 62, rouge: 35 },
      style: { blanc_vif: 0.8 },
      raisons: {
        blanc_sec: "L'œuf enrobe le palais et éteint les vins trop puissants : un blanc sec, léger et vif, reste le seul confortable."
      } },

    /* ---------------------- COCHON DE LAIT & RÔTIS ENTIERS --------- */
    { id: 'cochon_lait', etape: 'plat', nom: 'Cochon de lait et rôtis entiers', registre: 2,
      mots: ['cochinillo', 'cochon de lait', 'porcelet', 'agneau de lait', 'mechoui',
             'roti entier', 'lechon', 'porchetta', 'eisbein', 'jarret de porc'],
      axes: { gras: 2.5, sel: 2, puissance: 2.2, fume: 1 },
      familles: { rouge: 88, blanc_riche: 62, rose: 55, effervescent: 50, blanc_sec: 35 },
      style: { rouge_leger: 0.6 },
      raisons: {
        rouge: "Une peau croustillante et une chair très grasse : un rouge fruité et frais, pas trop tannique, coupe le gras sans écraser une viande délicate.",
        blanc_riche: "L'accord des amateurs : un grand blanc ample tient tête au gras avec plus de fraîcheur qu'un rouge."
      } },

    /* ---------------------- BROCHETTES DU LEVANT ------------------- */
    { id: 'brochette_levant', etape: 'plat', nom: 'Brochettes et grillades du Levant',
      mots: ['chich taouk', 'chiche taouk', 'chich kebab', 'kebab brochette', 'shawarma',
             'shish', 'adana', 'kofte', 'kefta', 'cevapi', 'cevapcici', 'pljeskavica'],
      axes: { epice: 1.5, sel: 2, gras: 1.8, fume: 1.8, puissance: 1.8 },
      familles: { rouge: 78, rose: 82, blanc_sec: 60, biere: 60, effervescent: 45 },
      style: { rouge_leger: 0.8, rose_vif: 0.7 },
      raisons: {
        rose: "Épices douces, citron et grillade : un rosé de caractère est plus juste qu'un rouge, qui durcirait sur le cumin et le sumac.",
        rouge: "Un rouge souple et épicé, servi frais, tient la brochette d'agneau sans lutter contre les épices."
      } },

    /* ---------------------- EMPANADAS & CHAUSSONS ------------------ */
    /* La choucroute était lue comme un simple plat de porc — donc du
       rouge, ce qu'aucun caviste ne sert dessus. Le chou est acide, le
       lard est salé, et l'Alsace a répondu à la question il y a
       longtemps : un blanc sec et vif, ou une bière. */
    { id: 'choucroute', etape: 'plat', nom: 'Choucroute et plats au chou',
      mots: ['choucroute', 'choucroute garnie', 'sauerkraut', 'chou braise', 'chou au lard'],
      axes: { acide: 2.2, sel: 2.5, gras: 2.2, umami: 1.5, puissance: 1.8 },
      familles: { blanc_sec: 94, biere: 84, effervescent: 62, rose: 45, rouge: 22 },
      style: { blanc_vif: 1.2 },
      supplante: ['porc', 'potee'],
      terroir: ['alsace', 'riesling', 'sylvaner'],
      deconseille: { rouge: 'Le chou fermenté est acide et le lard est salé : un rouge y devient dur et métallique.' },
      raisons: {
        blanc_sec: "Un riesling ou un sylvaner sec, tendu, tranche le gras du lard et répond à l'acidité du chou — l'accord alsacien, et il n'a jamais été détrôné.",
        biere: "Une blonde franche fait le même travail que le blanc, en plus désaltérant sur une assiette copieuse."
      } },

    /* ⚠️ UNE FONDUE BOURGUIGNONNE N'A PAS DE FROMAGE. C'est du bœuf cru
       cuit dans l'huile bouillante, servi avec des sauces froides — la
       fondue au fromage, c'est la SAVOYARDE. Erreur signalée par
       Grégoire : le mot « fondue » suffisait à ranger le plat avec la
       raclette et la tartiflette, et l'assistant conseillait un blanc de
       Savoie sur de la viande rouge. La fondue chinoise (bœuf au
       bouillon) et la pierrade tombaient dans le même piège.
       Cet archétype SUPPLANTE donc la raclette : dès que « fondue
       bourguignonne » est écrit, la lecture fromagère disparaît. */
    { id: 'fondue_viande', etape: 'plat', nom: 'Fondue bourguignonne, chinoise et pierrade', registre: 2,
      mots: ['fondue bourguignonne', 'fondu bourguignonne', 'fondue chinoise', 'fondu chinoise',
             'fondue vigneronne', 'pierrade', 'viande a l huile', 'fondue de viande'],
      axes: { gras: 2.5, tanin: 2, sel: 1.8, umami: 1.6, acide: 1.4, puissance: 2 },
      familles: { rouge: 92, rose: 58, biere: 48, effervescent: 28, blanc_sec: 20 },
      style: { rouge_leger: 0.9 },
      supplante: ['raclette', 'plat_mijote', 'grillades'],
      /* Porte fermée : le fromage n'entre pas dans ce plat, le blanc n'a
         donc aucune raison d'y entrer non plus. */
      couleurs_admises: ['rouge', 'rose'],
      deconseille: { blanc_sec: 'Il n’y a pas de fromage dans une fondue bourguignonne : c’est de la viande rouge frite, elle appelle un rouge.' },
      raisons: {
        rouge: "Du bœuf saisi dans l'huile et des sauces relevées : il faut un rouge fruité et frais, assez souple pour ne pas durcir sur la friture, assez présent pour tenir la viande. Un pinot ou un bordeaux de soif plutôt qu'un vin de garde tannique.",
        rose: "Un rosé de gastronomie, bien mûr, passe les sauces froides — béarnaise, tartare, cocktail — mieux qu'un rouge trop sérieux."
      } },

    /* ⚠️ UNE BLANQUETTE N'EST PAS UN PLAT MIJOTÉ ROUGE. C'est du veau
       en sauce blanche montée à la crème et au jaune d'œuf : chair
       pâle, aucun jus brun, aucun tanin à opposer. Le moteur la rangeait
       avec le bourguignon et conseillait un Pécharmant — un rouge
       tannique sur une sauce crémée, c'est la faute classique. */
    { id: 'blanquette', etape: 'plat', nom: 'Blanquette et fricassées en sauce blanche', registre: 2,
      mots: ['blanquette', 'blanquette de veau', 'fricassee', 'veau a la creme',
             'poulet a la creme', 'volaille a la creme', 'sauce supreme', 'sauce blanche',
             'vol au vent', 'bouchee a la reine', 'waterzooi'],
      axes: { gras: 2.8, umami: 1.8, sel: 1.2, acide: 1, puissance: 1.6 },
      familles: { blanc_riche: 94, blanc_sec: 82, effervescent: 62, champagne: 60, rose: 45, rouge: 18 },
      supplante: ['plat_mijote', 'veau'],
      deconseille: { rouge: 'Le tanin d’un rouge durcit sur une sauce crémée et masque une chair pâle et délicate.' },
      raisons: {
        blanc_riche: "Une sauce montée à la crème et au jaune d'œuf demande un blanc ample, avec assez d'acidité pour ne pas s'alourdir : c'est l'accord que le plat réclame depuis toujours.",
        champagne: "Les bulles allègent la crème et relancent la bouche entre deux cuillerées — l'accord de fête sur une blanquette."
      } },

    /* LA TRUFFE N'EST PAS UN CHAMPIGNON COMME LES AUTRES. Elle était
       rangée avec les cèpes, en registre 2 : l'assistant conseillait un
       vin de France à 5,90 € sur une omelette à la truffe. Un caviste ne
       fait pas ça. C'est un produit de fête (registre 3) qui appelle un
       vin mûr — grand blanc ou rouge de garde assoupli — et il faut
       aussi qu'il soit d'un rang à la hauteur. */
    { id: 'truffe', etape: 'plat', nom: 'Truffe', registre: 3,
      mots: ['truffe', 'truffes', 'truffe noire', 'brumale', 'melanosporum',
             'omelette a la truffe', 'brouillade de truffe', 'truffee', 'truffes rapees'],
      axes: { umami: 3, gras: 2.2, fume: 1, puissance: 2.4 },
      familles: { blanc_riche: 92, rouge: 88, champagne: 70, blanc_sec: 45, vdn: 25 },
      supplante: ['champignons', 'omelette', 'oeufs'],
      raisons: {
        blanc_riche: "La truffe est un parfum, pas une saveur : il lui faut un blanc mûr, ample et sans bois agressif, qui la porte au lieu de la couvrir.",
        rouge: "Un rouge de garde aux tanins fondus — un Pomerol, un Pécharmant d'âge — rejoint la truffe sur le terrain du sous-bois.",
        champagne: "Un champagne vineux et vieilli sur lattes partage ses notes de sous-bois avec la truffe : accord rare et spectaculaire."
      } },

    { id: 'empanada', etape: 'apero', nom: 'Empanadas et chaussons farcis',
      mots: ['empanada', 'empanadas', 'chausson farci', 'samoussa', 'samossa', 'bourek', 'borek',
             'pastel', 'friand', 'kibbeh', 'kebbe', 'takoyaki', 'karaage', 'banh xeo', 'croquetas', 'croqueta', 'bitterballen', 'kroket'],
      axes: { gras: 2, sel: 1.8, epice: 1, puissance: 1.4 },
      familles: { rouge: 68, rose: 76, blanc_sec: 72, effervescent: 70, biere: 62, champagne: 55 },
      raisons: {
        effervescent: "Une pâte grasse et une farce salée : les bulles allègent la bouchée et relancent l'appétit.",
        rose: "Un rosé sec accompagne toutes les farces à la fois — c'est le vin des plateaux où l'on picore."
      } }
  ];

  /* -------------------------------------------------------------------
     INGRÉDIENTS ISOLÉS
     Quand un plat n'est rattaché à aucun archétype, on lit ses
     ingrédients pour en déduire les axes. Un « velouté de potimarron au
     lard » n'a pas d'archétype, mais « lard » et « veloute » suffisent à
     l'orienter. Chaque entrée pousse les axes indiqués.
     ------------------------------------------------------------------- */
  S.ingredients = [
    { mots: ['creme', 'beurre', 'gratin', 'bechamel', 'mayonnaise'], axes: { gras: 2 } },
    { mots: ['citron', 'vinaigre', 'agrume', 'cornichon', 'capre'],  axes: { acide: 2 } },
    { mots: ['miel', 'sucre', 'caramel', 'confit', 'sirop', 'figue'],axes: { sucre: 2 } },
    { mots: ['piment', 'curry', 'gingembre', 'harissa', 'wasabi'],   axes: { epice: 2 } },
    { mots: ['soja', 'parmesan', 'anchois', 'tomate cuite', 'miso'], axes: { umami: 2 } },
    { mots: ['fume', 'grille', 'barbecue', 'braise', 'plancha'],     axes: { fume: 2 } },
    { mots: ['olive', 'anchois', 'lard', 'lardon', 'bacon', 'sel'],  axes: { sel: 2 } },
    { mots: ['endive', 'cacao', 'cafe', 'chicoree', 'roquette'],     axes: { amer: 2 } },
    { mots: ['boeuf', 'agneau', 'gibier', 'canard'],                 axes: { tanin: 2, puissance: 1 } },
    { mots: ['poisson', 'crevette', 'moule', 'algue', 'crustace'],   axes: { iode: 2 } }
  ];

  /* -------------------------------------------------------------------
     INGRÉDIENTS DE COCKTAIL → FAMILLE DE BOISSON
     ---------------------------------------------------------------------
     Table volontairement EXPLICITE, et non déduite des mots de famille.
     « Vermouth rouge » contient le mot « rouge » : s'en remettre à la
     détection générale ferait proposer un bordeaux pour un Negroni.
     Ce qui ne figure pas ici n'est rattaché à aucun produit — et c'est
     le comportement voulu : le sucre, la menthe, l'Angostura et le zeste
     d'orange ne s'achètent pas chez un caviste, et le vermouth n'est pas
     au catalogue. Mieux vaut « pas au catalogue » qu'une fausse piste.
     Pour vendre un jour le vermouth, il suffira d'ajouter une ligne.
     ------------------------------------------------------------------- */
  S.ingredients_cocktail = [
    { mots: ['rhum blanc', 'rhum ambre', 'rhum vieux', 'rhum agricole', 'rhum', 'rum'], famille: 'rhum' },
    { mots: ['whisky', 'whiskey', 'bourbon', 'scotch', 'rye'], famille: 'whisky' },
    { mots: ['gin'], famille: 'gin' },
    { mots: ['vodka'], famille: 'vodka' },
    { mots: ['tequila', 'mezcal'], famille: 'tequila' },
    { mots: ['cognac'], famille: 'cognac' },
    { mots: ['armagnac'], famille: 'armagnac' },
    { mots: ['calvados'], famille: 'calvados' },
    { mots: ['pastis', 'anisette', 'absinthe'], famille: 'pastis' },
    { mots: ['champagne'], famille: 'champagne' },
    { mots: ['prosecco', 'cremant', 'mousseux', 'vin petillant', 'pet nat'], famille: 'effervescent' },
    { mots: ['vin blanc'], famille: 'blanc_sec' },
    { mots: ['vin rouge'], famille: 'rouge' },
    { mots: ['vin rose'], famille: 'rose' },
    { mots: ['porto', 'banyuls', 'maury'], famille: 'vdn' },
    { mots: ['liqueur', 'triple sec', 'cointreau', 'curacao', 'creme de', 'chartreuse', 'limoncello'], famille: 'liqueur' },
    { mots: ['biere', 'ginger beer'], famille: 'biere' },
    { mots: ['tonic', 'soda', 'eau gazeuse', 'eau petillante'], famille: 'tonic' },
    { mots: ['jus de', 'jus d', 'nectar'], famille: 'jus' },
    { mots: ['the vert', 'the noir', 'infusion'], famille: 'the' }
  ];

  /* Un cocktail n'appelle pas la bouteille de collection : au-delà de ce
     multiple du prix médian de la famille, une référence n'est plus
     proposée comme ingrédient. */
  S.plafond_prix_cocktail = 2;

  /* -------------------------------------------------------------------
     QUESTIONS DE LEVÉE DE DOUTE
     Posées seulement si la saisie est réellement ambiguë, et jamais plus
     d'une par étape : l'outil doit rester rapide (règle n°17).
     ------------------------------------------------------------------- */
  S.questions = [
    { si: ['viande', 'boeuf', 'veau', 'porc'], sauf: ['grille', 'sauce', 'mijote', 'roti'],
      question: 'Cette viande est plutôt…', options: [
        { texte: 'Grillée ou rôtie', axes: { fume: 2, tanin: 1 } },
        { texte: 'En sauce ou mijotée', axes: { umami: 2, gras: 1 } } ] },
    { si: ['poisson'], sauf: ['grille', 'cru', 'sauce', 'fume', 'sushi'],
      question: 'Le poisson est servi…', options: [
        { texte: 'Cru ou mariné', axes: { iode: 2, acide: 1 } },
        { texte: 'Grillé ou rôti', axes: { fume: 1, iode: 1 } },
        { texte: 'En sauce', axes: { gras: 2 } } ] },
    { si: ['chocolat'], sauf: ['noir', 'lait', 'blanc'],
      question: 'Le chocolat est…', options: [
        { texte: 'Noir et corsé', axes: { amer: 2, puissance: 1 } },
        { texte: 'Au lait, plus doux', axes: { sucre: 2, amer: -1 } } ] },
    { si: ['curry', 'thai', 'indien', 'mexicain'], sauf: ['doux', 'epice', 'fort', 'pimente'],
      question: 'Ce plat est…', options: [
        { texte: 'Doux', axes: { epice: 0 } },
        { texte: 'Relevé', axes: { epice: 3 } } ] }
  ];

  /* -------------------------------------------------------------------
     SAISONS ET MOMENTS
     La saison oriente, elle ne décide jamais : le bonus est faible
     exprès (règle n°22 — la saisonnalité ne remplace pas la pertinence).
     ------------------------------------------------------------------- */
  S.saisons = [
    { id: 'hiver',     mois: [12, 1, 2],  favorise: ['rouge', 'whisky', 'armagnac', 'liquoreux'], mots: ['raclette', 'truffe', 'gibier'] },
    { id: 'printemps', mois: [3, 4, 5],   favorise: ['blanc_sec', 'rose', 'effervescent'],        mots: ['asperge', 'agneau'] },
    { id: 'ete',       mois: [6, 7, 8],   favorise: ['rose', 'blanc_sec', 'effervescent', 'biere'], mots: ['barbecue', 'salade', 'melon'] },
    { id: 'automne',   mois: [9, 10, 11], favorise: ['rouge', 'blanc_riche', 'armagnac'],         mots: ['cepe', 'gibier', 'chataigne'] }
  ];

  S.moments = [
    { id: 'noel',          mots: ['noel', 'reveillon', 'nouvel an', 'saint sylvestre'], favorise: ['champagne', 'liquoreux', 'rouge'], bulles: true },
    { id: 'paques',        mots: ['paques'],            favorise: ['rouge', 'blanc_sec'] },
    { id: 'saint_valentin',mots: ['saint valentin', 'valentin'], favorise: ['champagne', 'effervescent'], bulles: true },
    { id: 'barbecue',      mots: ['barbecue', 'bbq', 'plancha'], favorise: ['rose', 'rouge', 'biere'] },
    { id: 'raclette',      mots: ['raclette', 'fondue'], favorise: ['blanc_sec'] }
  ];

  /* -------------------------------------------------------------------
     FORMATS DE BOUTEILLE
     `cl` sert au calcul ; `mots` sert à reconnaître le format dans le
     titre d'une variante ou d'un produit.
     ------------------------------------------------------------------- */
  S.formats = [
    { id: 'demi',    nom: 'Demi-bouteille', cl: 37.5, mots: ['37.5', '375', 'demi-bouteille', 'demi bouteille', '50cl', '50 cl'] },
    { id: 'std',     nom: 'Bouteille 75 cl', cl: 75,  mots: ['75cl', '75 cl', 'bouteille'] },
    { id: 'magnum',  nom: 'Magnum',         cl: 150,  mots: ['magnum', '150cl', '1.5l', '1,5l'] },
    { id: 'jeroboam',nom: 'Jéroboam',       cl: 300,  mots: ['jeroboam', 'jéroboam', '3l'] },
    { id: 'bib',     nom: 'Bag-in-box',     cl: 300,  mots: ['bib', 'bag in box', 'cubi', 'fontaine'] },
    { id: 'spirit',  nom: 'Bouteille 70 cl',cl: 70,   mots: ['70cl', '70 cl'] },
    { id: 'biere',   nom: 'Bouteille 33 cl',cl: 33,   mots: ['33cl', '33 cl'] }
  ];

  /* Un magnum n'est proposé que s'il apporte quelque chose : à partir de
     deux bouteilles du même vin, et si le format existe réellement. */
  S.seuil_magnum = 2;

  /* -------------------------------------------------------------------
     REGISTRE D'UN PLAT — le plancher de prix
     ---------------------------------------------------------------------
     Une côte de bœuf ou un foie gras ne se servent pas avec la bouteille
     la moins chère de la cave : le client le lit comme un manque de
     considération, et il a raison. Chaque plat porte donc un registre,
     et le registre écarte le bas de la gamme AVANT de composer
     l'éventail.

     La valeur est une PART de la liste des candidats triée par prix, que
     l'on saute par le bas — jamais un montant en euros. Elle suit donc la
     cave : si les prix montent, le plancher monte avec eux, sans réglage.

       1 · quotidien ... aucun plancher (pizza, salade, apéritif simple)
       2 · soigné ...... on écarte le quart le plus bas
       3 · grand jour .. on écarte les 45 % les plus bas

     Sans registre déclaré, un plat vaut 1 : on ne prive jamais personne
     d'une bouteille abordable par excès de zèle.
     ------------------------------------------------------------------- */
  S.planchers = { 1: 0, 2: 0.25, 3: 0.45 };

  /* Marge de sécurité : part du volume ajoutée pour les repas où l'on
     ne veut pas manquer. Reste une SUGGESTION affichée à part, jamais
     ajoutée en douce au total (règle n°23). */
  S.marge_securite = 0.15;

  /* -------------------------------------------------------------------
     FILTRES RAPIDES DE REMPLACEMENT (règle n°10)
     `axe` indique au moteur ce qu'il doit privilégier lors du
     remplacement d'une suggestion.
     ------------------------------------------------------------------- */
  S.filtres = [
    { id: 'moins_cher',   nom: 'Moins cher',      axe: 'prix_bas' },
    { id: 'prestige',     nom: 'Plus prestigieux',axe: 'prix_haut' },
    { id: 'fruite',       nom: 'Plus fruité',     mots: ['fruite', 'fruit', 'gourmand', 'rond'] },
    { id: 'sec',          nom: 'Plus sec',        mots: ['sec', 'vif', 'tendu', 'mineral'] },
    { id: 'leger',        nom: 'Plus léger',      axe: 'puissance_bas' },
    { id: 'puissant',     nom: 'Plus puissant',   axe: 'puissance_haut' },
    { id: 'original',     nom: 'Plus original',   axe: 'rare' },
    { id: 'local',        nom: 'Plus local',      mots: ['perigord', 'sud-ouest', 'bergerac', 'montravel', 'pecharmant', 'dordogne'] },
    { id: 'bio',          nom: 'Bio',             mots: ['bio', 'biologique', 'biodynamie', 'nature'] },
    { id: 'sans_alcool',  nom: 'Sans alcool',     axe: 'sans_alcool' }
  ];

  /* -------------------------------------------------------------------
     TEXTES PAR DÉFAUT
     Tous surchargeables depuis l'éditeur Shopify : ce qui est écrit ici
     n'est qu'un filet de sécurité si un réglage est vidé.
     ------------------------------------------------------------------- */
  S.textes = {
    label_menu: "Quel est votre menu ou plat(s) ?",
    label_conso: "À combien estimez-vous la consommation de vos convives ?",
    mention_legale: "Les quantités indiquées sont des repères, pas une prescription : ajustez-les à vos convives. L'abus d'alcool est dangereux pour la santé, à consommer avec modération.",
    aucun_produit: "Nous n'avons pas de bouteille réellement disponible qui corresponde à cette étape aujourd'hui. Un caviste peut vous proposer une alternative.",
    recette_proche: "Recette similaire",
    recette_exacte: "La recette de la cave",
    accord_incertain: "Accord proposé par ressemblance : à confirmer avec un caviste si le plat est particulier."
  };

  w.CLSommelierSavoir = S;
})(window);
